import '@fontsource-variable/plus-jakarta-sans/index.css';
import './tailwind-ui.js';

import { MaterialsAdmin } from './materials-admin.js';
import { OfferItemsManager } from './offer-items.js';
import { OffersListManager } from './offers-list.js';
import { OfferEditorManager } from './offer-editor.js';
import { PublicOfferManager } from './public-offer.js';
import { PublicFilesManager } from './public-files.js';
import { CustomersManager } from './customers.js';
import { OrdersListManager } from './orders-list.js';
import { InvoicesListManager } from './invoices-list.js';
import { VyrobaManager } from './vyroba.js';
import { DashboardManager } from './dashboard.js';
import { FilesManager } from './files-manager.js';
import { UsersAdmin } from './users-admin.js';
import { ApiKeysAdmin } from './api-keys-admin.js';
import { OAuthClientsAdmin } from './oauth-clients-admin.js';
import { initAiAssistant } from './ai-assistant.js';
import { initAutoRefresh } from './auto-refresh.js';
import { initRealtimePush } from './realtime-push.js';
import { installSessionGuard, noteSessionRestored } from './auth-session.js';
import { jednotkyVazby, prepocetJednotek, validaceNasobku } from './jednotky.js';
import { icon, hydrateIcons } from './icons.js';
import { openPriceChartModal, cenaMarginalni, pointsFromFn, chipsFromFn } from './shared/price-chart.js';

installSessionGuard();

// Hydratace statických ikon (<svg data-icon="...">) v index.html
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hydrateIcons());
} else {
  hydrateIcons();
}

// SEC-11: Centrální obal pro window.fetch (a XMLHttpRequest) – musí být
// nainstalován co nejdříve, aby platil i pro ostatní moduly (customers.js,
// offer-editor.js, users-admin.js, files-manager.js, ...).
// Zajišťuje:
//  1. credentials: 'include' pro všechna API volání (auth nese httpOnly cookie),
//  2. CSRF hlavičku X-CSRF-Token (double-submit cookie) pro mutační metody,
//  3. detekci expirované session přes 401 (nahrazuje guard z auth-session.js,
//     který se řídil localStorage authToken – ten už neukládáme, viz SEC-7).
(function installApiFetchWrapper() {
  const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
  // Login vrací 401 při špatných údajích – to není expirovaná session.
  const AUTH_PASSTHROUGH = ['/api/auth/login'];

  // Vrátí pathname, pokud jde o volání našeho API (relativní /api/... nebo
  // absolutní localhost API v dev). Externí URL (např. Vercel Blob upload)
  // vrací null – tam CSRF hlavičku ani credentials přidávat nesmíme.
  const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
  const getApiPathname = (input) => {
    try {
      const raw = typeof input === 'string' ? input : (input && input.url) || '';
      const parsed = new URL(raw, window.location.href);
      if (!parsed.pathname.startsWith('/api/')) return null;
      const isOurHost =
        parsed.origin === window.location.origin ||
        (LOCAL_HOSTS.includes(window.location.hostname) && LOCAL_HOSTS.includes(parsed.hostname));
      return isOurHost ? parsed.pathname : null;
    } catch {
      return null;
    }
  };

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const hasStoredUser = () => Boolean(localStorage.getItem('currentUser'));

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function csrfFetch(input, init) {
    const apiPath = getApiPathname(input);
    let options = init;

    if (apiPath) {
      options = { ...(init || {}) };
      if (options.credentials === undefined) {
        options.credentials = 'include';
      }
      const method = String(
        options.method || (typeof input !== 'string' && input && input.method) || 'GET'
      ).toUpperCase();
      if (MUTATING_METHODS.includes(method)) {
        const token = readCookie('csrfToken');
        if (token) {
          const headers = new Headers(
            options.headers || (typeof input !== 'string' && input && input.headers) || undefined
          );
          if (!headers.has('X-CSRF-Token')) {
            headers.set('X-CSRF-Token', token);
          }
          options.headers = headers;
        }
      }
    }

    const response = await originalFetch(input, options);

    try {
      if (
        response.status === 401 &&
        apiPath &&
        !AUTH_PASSTHROUGH.some((p) => apiPath.startsWith(p)) &&
        hasStoredUser()
      ) {
        window.dispatchEvent(new CustomEvent('auth:session-expired', { detail: { reason: 'api-401' } }));
      }
    } catch {
      // Interceptor nikdy nesmí shodit původní fetch.
    }
    return response;
  };

  // files-manager.js používá XMLHttpRequest pro upload s progress barem –
  // i ten musí posílat CSRF hlavičku, jinak by mutace dostala 403.
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__csrfMethod = String(method || 'GET').toUpperCase();
    this.__csrfApiPath = getApiPathname(url);
    return originalOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__csrfApiPath && MUTATING_METHODS.includes(this.__csrfMethod)) {
      const token = readCookie('csrfToken');
      if (token) {
        try {
          this.setRequestHeader('X-CSRF-Token', token);
        } catch {
          // hlavičku nelze nastavit (špatný stav XHR) – necháme request projít
        }
      }
    }
    return originalSend.apply(this, args);
  };
})();

// Debug logging only in development (MUSÍ být na začátku!)
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const debugLog = (...args) => {
  if (isDev) console.log(...args);
};

// Globální flag pro detekci veřejné nabídky
window.isPublicOfferView = false;
window.publicOfferToken = null;
// Globální flag pro detekci veřejných souborů
window.isPublicFilesView = false;
window.publicFilesToken = null;

/** Skryje sidebar, mobilní menu a další admin chrome na veřejných odkazech. */
function setupPublicViewChrome() {
  document.body.classList.add('public-view');
  document.getElementById('auth-gate')?.remove();

  const sidebar = document.getElementById('app-sidebar');
  if (sidebar) {
    sidebar.classList.add('hidden');
    sidebar.classList.remove('lg:flex');
  }

  ['mobile-menu', 'mobile-menu-backdrop'].forEach((id) => {
    document.getElementById(id)?.classList.add('hidden');
  });

  const header = document.querySelector('header.app-bar-mobile');
  const footer = document.querySelector('footer.main-footer');
  const main = document.querySelector('main');

  if (header) header.style.display = 'none';
  if (footer) footer.style.display = 'none';
  if (main) {
    main.classList.remove('hidden');
    main.style.minHeight = '100vh';
    if (window.isPublicOfferView) {
      main.style.display = 'block';
      main.style.padding = '1.5rem 1rem';
    } else {
      main.style.display = 'block';
      main.style.padding = '0';
    }
  }
}

// Zkontrolovat URL routing ještě před DOMContentLoaded
const currentPath = window.location.pathname;
const publicOfferMatch = currentPath.match(/^\/public-offer\/([a-zA-Z0-9]+)$/);
if (publicOfferMatch) {
  window.isPublicOfferView = true;
  window.publicOfferToken = publicOfferMatch[1];
  debugLog('Detekována veřejná nabídka s tokenem:', window.publicOfferToken);
}
// Liberální regex – token validuje až backend, frontend jen rozezná routu
const publicFilesMatch = currentPath.match(/^\/public-files\/([^/]+)\/?$/);
if (publicFilesMatch) {
  window.isPublicFilesView = true;
  window.publicFilesToken = publicFilesMatch[1];
  debugLog('Detekována veřejná stránka souborů s tokenem:', window.publicFilesToken);
}

// Společný flag – jakýkoliv veřejný read-only pohled bez auth
window.isPublicView = () => window.isPublicOfferView || window.isPublicFilesView;

// Flag: aplikace je "odemčená" (session ověřena / login úspěšný) a data načtena.
// Dokud je false, přeskakují se API-závislé akce (pocitej apod.), aby
// nepřihlášený klient nestřílel 401 do chráněných endpointů.
window.__appReady = false;

// Inicializace layoutu po načtení DOM
document.addEventListener('DOMContentLoaded', async () => {
  // Veřejné stránky (/public-offer/*, /public-files/*) – jen obsah nabídky/souborů, bez admin UI
  if (window.isPublicOfferView || window.isPublicFilesView) {
    setupPublicViewChrome();
    return;
  }

  // Normální inicializace pro přihlášené uživatele
  // Nastavit fallback texty do info-boxů před načtením z API
  const materialsLastSavedElement = document.getElementById('materials-last-saved-info');
  const settingsLastSavedElement = document.getElementById('settings-last-saved-info');
  
  if (materialsLastSavedElement && !materialsLastSavedElement.textContent.trim()) {
    materialsLastSavedElement.textContent = '(načítám z API...)';
  }
  
  if (settingsLastSavedElement && !settingsLastSavedElement.textContent.trim()) {
    settingsLastSavedElement.textContent = '(načítám z API...)';
  }

  // POZOR: MaterialsAdmin + loadMaterialsToForm() + loadSettings() se spouští
  // až po ověření session / úspěšném loginu (viz bootAuthenticatedApp),
  // protože GET /api/materials i GET /api/settings vyžadují přihlášení.

  // Inicializovat podmíněné skrývání inputů
  setupConditionalInputs();
});

// Konfigurace API
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:1234/api'
  : '/api';

// Globální pojistka: zachyť 403 PASSWORD_CHANGE_REQUIRED z jakéhokoli /api/* volání
// a vyvolej event, na který reaguje AuthManager (forced password change modal).
// Řešíme to ve vrstvě app.js (nedotýkáme se auth-session.js). Response klonujeme,
// abychom nezkonzumovali tělo volajícímu.
(function installPasswordChangeGuard() {
  const isApi = (input) => {
    try {
      const u = typeof input === 'string' ? input : (input && input.url) || '';
      if (u.startsWith('/api/')) return true;
      return new URL(u, window.location.href).pathname.startsWith('/api/');
    } catch {
      return false;
    }
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function passwordGuardFetch(input, init) {
    const response = await originalFetch(input, init);
    try {
      if (response.status === 403 && isApi(input)) {
        const clone = response.clone();
        clone.json().then((data) => {
          if (data && data.code === 'PASSWORD_CHANGE_REQUIRED') {
            window.dispatchEvent(new CustomEvent('auth:password-change-required'));
          }
        }).catch(() => {});
      }
    } catch {
      // Interceptor nikdy nesmí shodit původní fetch.
    }
    return response;
  };
})();

// Authentication System
class AuthManager {
  constructor() {
    this.currentUser = null;
    this.init();
  }

  init() {
    // Veřejné stránky (/public-offer/*, /public-files/*) nemají auth gate
    // ani auth API volání – gate odstraňuje DOMContentLoaded handler výše.
    if (window.isPublicView()) return;

    // Migrace ze starého auth režimu: legacy Bearer token nesmí přebíjet
    // cookie-based session na některých API voláních.
    localStorage.removeItem('authToken');

    // Přidej event listenery
    this.setupEventListeners();

    // Auth gate bootstrap: setup wizard / ověření session / login formulář
    this.bootstrapGate();

    // Heartbeat: periodické ověření session přes cookie. Původní heartbeat
    // v auth-session.js se řídil localStorage authToken, který už neukládáme
    // (SEC-7), proto expiraci session hlídáme zde. Při 401 vyvolá centrální
    // fetch wrapper event 'auth:session-expired'.
    setInterval(() => {
      if (this.isLoggedIn()) this.verifySession();
    }, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isLoggedIn()) {
        this.verifySession();
      }
    });
  }

  // Bootstrap auth gate: rozhodne mezi setup wizardem, přihlášenou aplikací
  // a login formulářem. Gate (#auth-gate) je v HTML viditelný od prvního
  // renderu, obsah aplikace (header/main/footer) je skrytý třídou 'hidden'.
  async bootstrapGate() {
    // 1) First-run setup: pokud aplikace nemá žádný účet, zobraz wizard
    try {
      const response = await fetch(`${API_BASE_URL}/auth/setup-status`);
      const data = await response.json();
      if (response.ok && data?.data?.setupRequired === true) {
        this.showGateLogin();
        this.showSetupModal();
        return;
      }
    } catch (error) {
      debugLog('Nepodařilo se ověřit setup-status:', error);
      // Pokračujeme na ověření session – při výpadku backendu skončí na loginu
    }

    // 2) Ověření session přes httpOnly cookie
    const sessionUser = await this.fetchSessionUser();
    if (sessionUser) {
      // Session platí – obnov UI data (localStorage má víc detailů než /auth/check)
      let storedUser = null;
      try {
        storedUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      } catch {
        storedUser = null;
      }
      this.currentUser = storedUser || sessionUser;
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      this.unlockApp();
      if (this.currentUser.mustChangePassword === true) {
        this.showForcePasswordModal();
      }
    } else {
      // Session neplatí – ukliď zbytky a nech uživatele na login gate
      this.currentUser = null;
      localStorage.removeItem('currentUser');
      this.showGateLogin();
    }
  }

  // GET /auth/check – vrátí objekt uživatele při platné session, jinak null.
  // Vedlejší efekt: requireAuth na backendu nastaví/obnoví csrf cookie.
  async fetchSessionUser() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/check`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) return null;
      const data = await response.json().catch(() => null);
      return data?.data?.user || null;
    } catch {
      // Síťová chyba – chovej se jako nepřihlášený (uživatel se může přihlásit)
      return null;
    }
  }

  // Skryje gate, zobrazí aplikaci a spustí načtení dat závislých na API
  // (materials, settings vyžadují přihlášení). Spouští se jen jednou.
  unlockApp() {
    if (window.__appReady) return;
    window.__appReady = true;

    document.querySelector('header')?.classList.remove('hidden');
    document.querySelector('main')?.classList.remove('hidden');
    document.querySelector('footer')?.classList.remove('hidden');
    document.getElementById('app-sidebar')?.classList.remove('hidden');
    document.getElementById('auth-gate')?.remove();

    this.updateUI();

    // Kalkulátor je výchozí landing sekce po přihlášení / obnově session.
    if (typeof sectionManager !== 'undefined' && sectionManager) {
      sectionManager.switchToSection('kalkulator');
    }

    // Teprve teď volej chráněné API – materiály, nastavení, první kalkulace
    try {
      if (!window.materialsAdmin) {
        window.materialsAdmin = new MaterialsAdmin();
      }
    } catch (error) {
      console.error('Chyba při inicializaci MaterialsAdmin:', error);
    }

    Promise.allSettled([
      loadMaterialsToForm(),
      loadSettings()
    ]).then(() => {
      if (typeof sectionManager !== 'undefined' && sectionManager?.currentSection === 'kalkulator') {
        pocitej();
      }
    });

    // AI asistent – inicializace chatu až po přihlášení (endpointy vyžadují auth).
    try { initAiAssistant(); } catch (e) { console.error('AI asistent init:', e); }
  }

  // Přepne gate ze stavu "ověřuji" na login formulář
  showGateLogin() {
    const gate = document.getElementById('auth-gate');
    if (!gate) return;
    document.getElementById('gate-loading')?.classList.add('hidden');
    document.getElementById('gate-login-card')?.classList.remove('hidden');
    setTimeout(() => document.getElementById('gate-username')?.focus(), 50);
  }

  showSetupModal() {
    const modal = document.getElementById('setupModal');
    if (!modal) return;
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    setTimeout(() => document.getElementById('setupUsername')?.focus(), 50);
  }

  hideSetupModal() {
    const modal = document.getElementById('setupModal');
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  async handleSetup(e) {
    e.preventDefault();
    const errorBox = document.getElementById('setupError');
    const submitBtn = document.getElementById('setupSubmitBtn');
    const showSetupError = (msg) => {
      if (errorBox) {
        errorBox.textContent = msg;
        errorBox.classList.remove('hidden');
      }
    };
    if (errorBox) errorBox.classList.add('hidden');

    const username = document.getElementById('setupUsername').value.trim();
    const name = document.getElementById('setupName').value.trim();
    const email = document.getElementById('setupEmail').value.trim();
    const phone = document.getElementById('setupPhone').value.trim();
    const password = document.getElementById('setupPassword').value;
    const passwordConfirm = document.getElementById('setupPasswordConfirm').value;

    const pwdError = validatePasswordStrength(password);
    if (pwdError) {
      showSetupError(pwdError);
      return;
    }
    if (password !== passwordConfirm) {
      showSetupError('Hesla se neshodují.');
      return;
    }

    submitBtn?.setAttribute('disabled', 'true');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, name, password, email, phone })
      });
      const data = await response.json();
      if (response.ok) {
        this.hideSetupModal();
        const form = document.getElementById('setupForm');
        if (form) form.reset();
        showNotification('Administrátor vytvořen, přihlaste se', 'success');
        // Po setupu zůstává viditelný auth gate s login formulářem
        this.showGateLogin();
        const gateUsername = document.getElementById('gate-username');
        if (gateUsername) gateUsername.value = username;
        setTimeout(() => document.getElementById('gate-password')?.focus(), 50);
      } else {
        showSetupError(data.message || 'Nepodařilo se vytvořit administrátora');
      }
    } catch (error) {
      console.error('Setup error:', error);
      showSetupError('Chyba spojení se serverem');
    } finally {
      submitBtn?.removeAttribute('disabled');
    }
  }

  // Vynucená změna hesla (mustChangePassword nebo 403 PASSWORD_CHANGE_REQUIRED)
  showForcePasswordModal() {
    const modal = document.getElementById('forcePasswordModal');
    if (!modal) return;
    if (!modal.classList.contains('hidden')) return; // už je vidět
    const errorBox = document.getElementById('forcePasswordError');
    if (errorBox) errorBox.classList.add('hidden');
    const form = document.getElementById('forcePasswordForm');
    if (form) form.reset();
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    setTimeout(() => document.getElementById('forceOldPassword')?.focus(), 50);
  }

  hideForcePasswordModal() {
    const modal = document.getElementById('forcePasswordModal');
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  async handleForcePasswordChange(e) {
    e.preventDefault();
    const errorBox = document.getElementById('forcePasswordError');
    const submitBtn = document.getElementById('forcePasswordSubmitBtn');
    const showError = (msg) => {
      if (errorBox) {
        errorBox.textContent = msg;
        errorBox.classList.remove('hidden');
      }
    };
    if (errorBox) errorBox.classList.add('hidden');

    const oldPassword = document.getElementById('forceOldPassword').value;
    const newPassword = document.getElementById('forceNewPassword').value;
    const confirmPassword = document.getElementById('forceConfirmPassword').value;

    if (!oldPassword) {
      showError('Zadejte své dočasné heslo.');
      return;
    }
    const pwdError = validatePasswordStrength(newPassword);
    if (pwdError) {
      showError(pwdError);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Nová hesla se neshodují.');
      return;
    }

    submitBtn?.setAttribute('disabled', 'true');
    try {
      const response = await fetch(`${API_BASE_URL}/user/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ oldPassword, newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        // Aktualizuj příznak na uživateli
        if (this.currentUser) {
          this.currentUser.mustChangePassword = false;
          localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
        }
        this.hideForcePasswordModal();
        showNotification('Heslo bylo úspěšně změněno', 'success');
      } else {
        showError(data.message || 'Chyba při změně hesla');
      }
    } catch (error) {
      console.error('Force password change error:', error);
      showError('Chyba spojení se serverem');
    } finally {
      submitBtn?.removeAttribute('disabled');
    }
  }

  setupEventListeners() {
    // Login form submit (full-screen auth gate)
    const gateLoginForm = document.getElementById('gate-login-form');
    if (gateLoginForm) {
      gateLoginForm.addEventListener('submit', (e) => this.handleLogin(e));
    }

    // Logout event listeners - desktop, mobile and dropdown
    document.addEventListener('click', (e) => {
      if (!e.target || typeof e.target.closest !== 'function') return;

      if (e.target.closest('#logout-btn, #logout-btn-mobile')) {
        debugLog('Logout tlačítko kliknuto');
        this.handleLogout();
        return;
      }

      // Profile settings button listeners
      if (e.target.closest('#profile-settings-btn, #profile-settings-btn-mobile')) {
        debugLog('Nastavení profilu tlačítko kliknuto');
        this.showProfileSettings();
      }
    });

    // Session expired – posloucháme event ze SessionGuard.
    // Během bootstrapu gate (aplikace ještě není odemčená) event ignorujeme:
    // 401 z /auth/check při neplatné session řeší bootstrapGate zobrazením loginu.
    window.addEventListener('auth:session-expired', () => {
      if (!window.__appReady) return;
      this.showSessionExpiredModal();
    });

    // Session expired modal: form submit + logout button
    const sessionForm = document.getElementById('sessionExpiredForm');
    if (sessionForm) {
      sessionForm.addEventListener('submit', (e) => this.handleSessionRelogin(e));
    }
    const sessionLogoutBtn = document.getElementById('sessionExpiredLogoutBtn');
    if (sessionLogoutBtn) {
      sessionLogoutBtn.addEventListener('click', () => {
        this.hideSessionExpiredModal();
        this.handleLogout();
      });
    }

    // First-run setup wizard
    const setupForm = document.getElementById('setupForm');
    if (setupForm) {
      setupForm.addEventListener('submit', (e) => this.handleSetup(e));
    }

    // Vynucená změna hesla
    const forcePasswordForm = document.getElementById('forcePasswordForm');
    if (forcePasswordForm) {
      forcePasswordForm.addEventListener('submit', (e) => this.handleForcePasswordChange(e));
    }

    // Globální pojistka: pokud API vrátí 403 PASSWORD_CHANGE_REQUIRED, vyvolej modal
    window.addEventListener('auth:password-change-required', () => this.showForcePasswordModal());
  }

  async handleLogin(e) {
    e.preventDefault();

    const submitBtn = document.getElementById('gate-login-submit');
    const errorDiv = document.getElementById('gate-login-error');
    if (errorDiv) errorDiv.classList.add('hidden');

    const formData = new FormData(e.target);
    const credentials = {
      username: formData.get('username'),
      password: formData.get('password')
    };

    submitBtn?.setAttribute('disabled', 'true');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // SEC-7: session token nese httpOnly cookie, do localStorage ukládáme
        // jen currentUser (UI data pro obnovu po reloadu, není to credential).
        localStorage.removeItem('authToken');
        localStorage.setItem('currentUser', JSON.stringify(data.data.user));

        this.currentUser = data.data.user;
        e.target.reset();

        // Ověř session přes cookie – zároveň tím backend nastaví csrf cookie
        // (nutné pro CSRF hlavičku u mutací, viz SEC-11).
        this.verifySession();

        // Skryj gate, zobraz aplikaci a spusť načtení dat (materials, settings)
        this.unlockApp();

        showNotification('Úspěšně přihlášen', 'success');

        // Vynucená změna hesla po prvním přihlášení (dočasné heslo)
        if (this.currentUser.mustChangePassword === true) {
          this.showForcePasswordModal();
        }
      } else {
        const fallback = response.status === 429
          ? 'Příliš mnoho pokusů o přihlášení, zkuste to později'
          : 'Chyba při přihlašování';
        this.showLoginError(data.message || fallback);
      }
    } catch (error) {
      console.error('Login error:', error);
      this.showLoginError('Chyba spojení se serverem');
    } finally {
      submitBtn?.removeAttribute('disabled');
    }
  }

  async handleLogout() {
    // Nejdřív vyčistíme stav, aby případné 401 z paralelních volání už SessionGuard nedetekoval
    this.currentUser = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');

    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Chyba při odhlašování:', error);
    }

    // Reload spolehlivě vrátí auth gate: obsah aplikace je v HTML defaultně
    // skrytý a bootstrapGate po neplatné session zobrazí login formulář.
    window.location.reload();
  }

  showProfileSettings() {
    // Zavři dropdown menu
    this.closeUserDropdown();
    
    // Přepni na sekci profilu
    if (sectionManager) {
      sectionManager.switchToSection('profil');
    }
    
    // Načti a zobraz data profilu
    this.loadProfileData();
  }

  async loadProfileData() {
    try {
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (response.ok) {
        // Naplň formulář daty
        document.getElementById('profile-username').value = data.data.username || '';
        document.getElementById('profile-role').value = data.data.role === 'admin' ? 'Administrátor' : 'Sales' || '';
        document.getElementById('profile-name').value = data.data.name || '';
        document.getElementById('profile-email').value = data.data.email || '';
        document.getElementById('profile-phone').value = data.data.phone || '';
        
        // Načti preference
        const showHourlyCalcCheckbox = document.getElementById('profile-show-hourly-calc');
        if (showHourlyCalcCheckbox) {
          showHourlyCalcCheckbox.checked = data.data.showHourlyCalc || false;
        }
      } else {
        showNotification(data.message || 'Chyba při načítání profilu', 'error');
      }
    } catch (error) {
      console.error('Chyba při načítání profilu:', error);
      showNotification('Chyba spojení se serverem', 'error');
    }
  }

  closeUserDropdown() {
    const dropdown = document.getElementById('user-dropdown-menu');
    if (dropdown) {
      dropdown.classList.add('opacity-0', 'invisible', 'scale-95');
    }
  }

  // Lehké ověření platnosti session přes httpOnly cookie.
  // Vedlejší efekt: requireAuth na backendu nastaví/obnoví csrf cookie,
  // takže následné mutace mohou poslat hlavičku X-CSRF-Token.
  async verifySession() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/check`, {
        method: 'GET',
        credentials: 'include'
      });
      return response.ok;
    } catch {
      // Síťová chyba není důvod uživatele odhlašovat
      return true;
    }
  }

  updateUI() {
    const userDropdown = document.getElementById('user-dropdown');
    const userInfoMobile = document.getElementById('user-info-mobile');
    const usernameDisplay = document.getElementById('username-display');
    const usernameDisplayMobile = document.getElementById('username-display-mobile');
    
    // Desktop menu items
    const nastaveniMenuItem = document.getElementById('nastaveni-menu-item');
    const nabidkyMenuItem = document.getElementById('nabidky-menu-item');
    const objednavkyMenuItem = document.getElementById('objednavky-menu-item');
    const vyrobaMenuItem = document.getElementById('vyroba-menu-item');
    const fakturyMenuItem = document.getElementById('faktury-menu-item');
    const zakazniciMenuItem = document.getElementById('zakaznici-menu-item');
    const souboryMenuItem = document.getElementById('soubory-menu-item');
    const papiryMenuItem = document.getElementById('papiry-menu-item');
    const uzivateleMenuItem = document.getElementById('uzivatele-menu-item');
    const aiMenuItem = document.getElementById('ai-asistent-menu-item');

    // Mobile menu items
    const nastaveniMenuItemMobile = document.getElementById('nastaveni-menu-item-mobile');
    const nabidkyMenuItemMobile = document.getElementById('nabidky-menu-item-mobile');
    const objednavkyMenuItemMobile = document.getElementById('objednavky-menu-item-mobile');
    const vyrobaMenuItemMobile = document.getElementById('vyroba-menu-item-mobile');
    const fakturyMenuItemMobile = document.getElementById('faktury-menu-item-mobile');
    const zakazniciMenuItemMobile = document.getElementById('zakaznici-menu-item-mobile');
    const souboryMenuItemMobile = document.getElementById('soubory-menu-item-mobile');
    const papiryMenuItemMobile = document.getElementById('papiry-menu-item-mobile');
    const uzivateleMenuItemMobile = document.getElementById('uzivatele-menu-item-mobile');
    const aiMenuItemMobile = document.getElementById('ai-asistent-menu-item-mobile');

    if (this.isLoggedIn()) {
      // Desktop UI
      userDropdown?.classList.remove('hidden');
      if (usernameDisplay) {
        usernameDisplay.textContent = this.currentUser.username;
      }
      
      // Mobile UI
      userInfoMobile?.classList.remove('hidden');
      if (usernameDisplayMobile) {
        usernameDisplayMobile.textContent = this.currentUser.username;
      }
      
      // Nastav vlastní dropdown funkcionalitu
      this.setupCustomDropdown();
      
      // Zobraz chráněné menu položky podle role - Desktop
      nabidkyMenuItem?.classList.remove('hidden');
      objednavkyMenuItem?.classList.remove('hidden');
      vyrobaMenuItem?.classList.remove('hidden');
      fakturyMenuItem?.classList.remove('hidden');
      zakazniciMenuItem?.classList.remove('hidden');
      souboryMenuItem?.classList.remove('hidden');
      papiryMenuItem?.classList.remove('hidden');
      aiMenuItem?.classList.remove('hidden');
      
      // Zobraz Nastavení pouze pro Administratory
      if (this.currentUser.role === 'admin') {
        nastaveniMenuItem?.classList.remove('hidden');
        uzivateleMenuItem?.classList.remove('hidden');
      } else {
        nastaveniMenuItem?.classList.add('hidden');
        uzivateleMenuItem?.classList.add('hidden');
      }
      
      // Zobraz chráněné menu položky podle role - Mobile
      nabidkyMenuItemMobile?.classList.remove('hidden');
      objednavkyMenuItemMobile?.classList.remove('hidden');
      vyrobaMenuItemMobile?.classList.remove('hidden');
      fakturyMenuItemMobile?.classList.remove('hidden');
      zakazniciMenuItemMobile?.classList.remove('hidden');
      souboryMenuItemMobile?.classList.remove('hidden');
      papiryMenuItemMobile?.classList.remove('hidden');
      aiMenuItemMobile?.classList.remove('hidden');
      
      // Zobraz Nastavení pouze pro Administratory (mobile)
      if (this.currentUser.role === 'admin') {
        nastaveniMenuItemMobile?.classList.remove('hidden');
        uzivateleMenuItemMobile?.classList.remove('hidden');
      } else {
        nastaveniMenuItemMobile?.classList.add('hidden');
        uzivateleMenuItemMobile?.classList.add('hidden');
      }
      
      // Přidat event listenery pro dropdown menu po přihlášení
      setTimeout(() => {
        const settingsDropdownBtn = document.getElementById('settings-dropdown-btn');
        const materialsDropdownBtn = document.getElementById('materials-dropdown-btn');
        
        if (settingsDropdownBtn) {
          settingsDropdownBtn.addEventListener('click', toggleSettingsDropdown);
        }
        
        if (materialsDropdownBtn) {
          materialsDropdownBtn.addEventListener('click', toggleMaterialsDropdown);
        }
      }, 100);
    } else {
      // Desktop UI
      userDropdown?.classList.add('hidden');
      
      // Mobile UI
      userInfoMobile?.classList.add('hidden');
      
      // Skryj chráněné menu položky - Desktop
      nastaveniMenuItem?.classList.add('hidden');
      uzivateleMenuItem?.classList.add('hidden');
      nabidkyMenuItem?.classList.add('hidden');
      objednavkyMenuItem?.classList.add('hidden');
      vyrobaMenuItem?.classList.add('hidden');
      fakturyMenuItem?.classList.add('hidden');
      zakazniciMenuItem?.classList.add('hidden');
      souboryMenuItem?.classList.add('hidden');
      papiryMenuItem?.classList.add('hidden');
      aiMenuItem?.classList.add('hidden');

      // Skryj chráněné menu položky - Mobile
      nastaveniMenuItemMobile?.classList.add('hidden');
      uzivateleMenuItemMobile?.classList.add('hidden');
      nabidkyMenuItemMobile?.classList.add('hidden');
      objednavkyMenuItemMobile?.classList.add('hidden');
      vyrobaMenuItemMobile?.classList.add('hidden');
      fakturyMenuItemMobile?.classList.add('hidden');
      zakazniciMenuItemMobile?.classList.add('hidden');
      souboryMenuItemMobile?.classList.add('hidden');
      papiryMenuItemMobile?.classList.add('hidden');
      aiMenuItemMobile?.classList.add('hidden');
    }
  }

  setupCustomDropdown() {
    const dropdownBtn = document.getElementById('user-dropdown-btn');
    const dropdownMenu = document.getElementById('user-dropdown-menu');
    const userDropdown = document.getElementById('user-dropdown');
    const dropdownArrow = document.getElementById('user-dropdown-arrow');
    
    if (dropdownBtn && dropdownMenu && userDropdown) {
      // Odstran existující event listenery
      dropdownBtn.removeEventListener('click', this.toggleDropdown);
      
      // Přidej nový event listener
      this.toggleDropdown = (e) => {
        e.stopPropagation();
        const isVisible = dropdownMenu.classList.contains('opacity-100');
        
        if (isVisible) {
          // Zavři dropdown
          dropdownMenu.classList.remove('opacity-100', 'visible', 'scale-100');
          dropdownMenu.classList.add('opacity-0', 'invisible', 'scale-95');
          if (dropdownArrow) {
            dropdownArrow.classList.remove('rotate-180');
          }
        } else {
          // Otevři dropdown
          dropdownMenu.classList.remove('opacity-0', 'invisible', 'scale-95');
          dropdownMenu.classList.add('opacity-100', 'visible', 'scale-100');
          if (dropdownArrow) {
            dropdownArrow.classList.add('rotate-180');
          }
        }
        
        debugLog('Dropdown toggle:', !isVisible);
      };
      
      dropdownBtn.addEventListener('click', this.toggleDropdown);
      
      // Zavři dropdown při kliknutí mimo
      document.addEventListener('click', (e) => {
        if (!userDropdown.contains(e.target)) {
          dropdownMenu.classList.remove('opacity-100', 'visible', 'scale-100');
          dropdownMenu.classList.add('opacity-0', 'invisible', 'scale-95');
          if (dropdownArrow) {
            dropdownArrow.classList.remove('rotate-180');
          }
        }
      });
      
      debugLog('Vlastní dropdown nastaven');
    }
  }

  showLoginError(message) {
    const errorDiv = document.getElementById('gate-login-error');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.remove('hidden');
    }
  }

  showSessionExpiredModal() {
    const modal = document.getElementById('sessionExpiredModal');
    if (!modal) return;
    if (!modal.classList.contains('hidden')) return; // už je vidět

    // Předvyplň jméno z aktuálního uživatele (pokud máme)
    const usernameInput = document.getElementById('sessionExpiredUsername');
    const passwordInput = document.getElementById('sessionExpiredPassword');
    const errorBox = document.getElementById('sessionExpiredError');
    const username = this.currentUser?.username
      || (() => {
        try { return JSON.parse(localStorage.getItem('currentUser') || '{}').username || ''; }
        catch { return ''; }
      })();

    if (usernameInput) usernameInput.value = username;
    if (passwordInput) {
      passwordInput.value = '';
    }
    if (errorBox) {
      errorBox.classList.add('hidden');
      errorBox.textContent = '';
    }

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    // Focus na heslo, aby šlo rovnou psát
    setTimeout(() => passwordInput?.focus(), 50);
  }

  hideSessionExpiredModal() {
    const modal = document.getElementById('sessionExpiredModal');
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  async handleSessionRelogin(e) {
    e.preventDefault();
    const usernameInput = document.getElementById('sessionExpiredUsername');
    const passwordInput = document.getElementById('sessionExpiredPassword');
    const errorBox = document.getElementById('sessionExpiredError');
    const submitBtn = document.getElementById('sessionExpiredSubmitBtn');
    const spinner = document.getElementById('sessionExpiredSpinner');

    const username = usernameInput?.value?.trim();
    const password = passwordInput?.value || '';

    if (!username || !password) {
      if (errorBox) {
        errorBox.textContent = 'Zadejte heslo.';
        errorBox.classList.remove('hidden');
      }
      return;
    }

    submitBtn?.setAttribute('disabled', 'true');
    spinner?.classList.remove('hidden');
    if (errorBox) {
      errorBox.classList.add('hidden');
      errorBox.textContent = '';
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();

      if (response.ok && data?.success && data?.data?.user) {
        // SEC-7: token neukládáme, auth nese httpOnly cookie
        localStorage.removeItem('authToken');
        localStorage.setItem('currentUser', JSON.stringify(data.data.user));
        this.currentUser = data.data.user;
        noteSessionRestored();
        // Obnov csrf cookie pro mutační requesty (SEC-11)
        this.verifySession();
        this.hideSessionExpiredModal();
        this.updateUI();
        showNotification('Přihlášení obnoveno', 'success');
        if (this.currentUser && this.currentUser.mustChangePassword === true) {
          this.showForcePasswordModal();
        }
      } else {
        const msg = data?.message || 'Nepodařilo se přihlásit';
        if (errorBox) {
          errorBox.textContent = msg;
          errorBox.classList.remove('hidden');
        }
        passwordInput?.focus();
        passwordInput?.select?.();
      }
    } catch (err) {
      console.error('Session relogin error:', err);
      if (errorBox) {
        errorBox.textContent = 'Chyba spojení se serverem';
        errorBox.classList.remove('hidden');
      }
    } finally {
      submitBtn?.removeAttribute('disabled');
      spinner?.classList.add('hidden');
    }
  }

  isLoggedIn() {
    return this.currentUser !== null;
  }

  getAuthHeaders() {
    // SEC-7: autentizaci nese httpOnly cookie (credentials: 'include' zajišťuje
    // centrální fetch wrapper), Bearer token z localStorage se už nepoužívá.
    return {};
  }
}

// Validace síly hesla – vrací chybovou hlášku (string) nebo null, pokud je heslo OK.
// Stejná pravidla jako v profilu: min 8 znaků, malé i velké písmeno, číslo, speciální znak.
function validatePasswordStrength(password) {
  if (!password || password.length < 8) {
    return 'Heslo musí mít alespoň 8 znaků';
  }
  if (!/[a-z]/.test(password)) {
    return 'Heslo musí obsahovat alespoň jedno malé písmeno';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Heslo musí obsahovat alespoň jedno velké písmeno';
  }
  if (!/[0-9]/.test(password)) {
    return 'Heslo musí obsahovat alespoň jedno číslo';
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return 'Heslo musí obsahovat alespoň jeden speciální znak (!@#$%^&* atd.)';
  }
  return null;
}

// Funkce pro načítání materiálů z API do formuláře
async function loadMaterialsToForm() {
  try {
    const response = await fetch(`${API_BASE_URL}/materials`);
    const result = await response.json();
    
    if (result.success) {
      // Nový formát dat s metadata wrapper
      const materials = result.data.materials;
      renderMaterialsToSelect(materials);
    } else {
      console.error('Chyba při načítání materiálů:', result.error);
    }
  } catch (error) {
    console.error('Chyba při načítání materiálů:', error);
  }
}

// Exportuji funkci globálně
window.loadMaterialsToForm = loadMaterialsToForm;

// Funkce pro zobrazení notifikace
function showNotification(message, type = 'success') {
  // Mapování typů na Tailwind třídy
  const typeClasses = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };

  // Vytvořím notifikaci
  const notification = document.createElement('div');
  notification.className = `fixed top-4 right-4 z-50 max-w-sm w-full ${typeClasses[type] || typeClasses.info} border rounded-lg shadow-lg p-4 transition-all duration-300 transform translate-x-0`;
  notification.innerHTML = `
    <div class="flex items-start">
      <div class="flex-1">
        <p class="text-sm font-medium">${message}</p>
      </div>
      <button type="button" onclick="this.parentElement.parentElement.remove()" class="ml-4 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none">
        ${icon('x-mark', 'h-5 w-5')}
      </button>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  // Animace při zobrazení
  setTimeout(() => {
    notification.classList.add('opacity-100');
  }, 10);
  
  // Automaticky odstraň po 3 sekundách
  setTimeout(() => {
    notification.classList.add('opacity-0', 'translate-x-full');
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }, 3000);
}

// Globální export
window.showNotification = showNotification;

// Funkce pro ukládání nastavení do API
async function saveSettings() {
  const settings = {
    // ============================================================
    // Firemní údaje (dodavatel) – sekce Nastavení → Firma.
    // Id inputů odpovídají klíčům, které čte isdocx-builder.js
    // (normalizeSupplier) a backend/pdf/renderer.js (buildSupplierFromSettings).
    // ============================================================
    companyName: getValue('#companyName', ''),
    ico: getValue('#ico', ''),
    dic: getValue('#dic', ''),
    street: getValue('#street', ''),
    buildingNumber: getValue('#buildingNumber', ''),
    city: getValue('#city', ''),
    postalZone: getValue('#postalZone', ''),
    countryCode: getValue('#countryCode', 'CZ'),
    bankAccount: getValue('#bankAccount', ''),
    bankCode: getValue('#bankCode', ''),
    iban: getValue('#iban', ''),
    swift: getValue('#swift', ''),
    bankName: getValue('#bankName', ''),
    registerRegister: getValue('#registerRegister', ''),
    registerNumber: getValue('#registerNumber', ''),
    companyPhone: getValue('#companyPhone', ''),
    companyEmail: getValue('#companyEmail', ''),
    companyWeb: getValue('#companyWeb', ''),

    // Základní nastavení
    koeficientCena: +getValue('#koeficientCena', 1.2),
    dph: +getValue('#dph', 1.21),
    // technologie - NEUKLÁDÁ SE DO CENTRÁLNÍHO NASTAVENÍ (je lokální v localStorage)
    obratka: getChecked('#obratka', true),
    
    // Okraje a rozměry
    okrajKolemProduktu: +getValue('#okrajKolemProduktu', 3),
    okrajKolemArchu: +getValue('#okrajKolemArchu', 7),
    horniOkraj: +getValue('#horniOkraj', 17),
    dolniOkraj: +getValue('#dolniOkraj', 15),
    pravyOkraj: +getValue('#pravyOkraj', 10),
    levyOkraj: +getValue('#levyOkraj', 10),
    okrajKolemArchuDigital: +getValue('#okrajKolemArchuDigital', 7),
    horniOkrajDigital: +getValue('#horniOkrajDigital', 0),
    dolniOkrajDigital: +getValue('#dolniOkrajDigital', 0),
    pravyOkrajDigital: +getValue('#pravyOkrajDigital', 0),
    levyOkrajDigital: +getValue('#levyOkrajDigital', 0),
    // Rozměry archů strojů (standardizované rozměry používaných papírů)
    ofsetRozmerSirka: +getValue('#ofsetRozmerSirka', 650),
    ofsetRozmerVyska: +getValue('#ofsetRozmerVyska', 480),
    digitalRozmerSirka: +getValue('#digitalRozmerSirka', 320),
    digitalRozmerVyska: +getValue('#digitalRozmerVyska', 450),
    
    // Ofset nastavení
    barvaCena: +getValue('#barvaCena', 500),
    mytiCena: +getValue('#mytiCena', 250),
    prujezdOfsetCena: +getValue('#prujezdOfsetCena', 0.25),
    obratkaCena: +getValue('#obratkaCena', 350),
    ofsetSoubehSleva: +getValue('#ofsetSoubehSleva', 0.8),
    
    // Digitál nastavení
    pripravaCernobile: +getValue('#pripravaCernobile', 200),
    pripravaBarva: +getValue('#pripravaBarva', 200),
    pripravaBarvaSpecial: +getValue('#pripravaBarvaSpecial', 0),
    prujezdDigiCenaDo750_44: +getValue('#prujezdDigiCenaDo750_44', 5.74),
    prujezdDigiCenaNad750_44: +getValue('#prujezdDigiCenaNad750_44', 3.3),
    prujezdDigiCenaDo750_40: +getValue('#prujezdDigiCenaDo750_40', 4.74),
    prujezdDigiCenaNad750_40: +getValue('#prujezdDigiCenaNad750_40', 2.3),
    prujezdDigiCenaDo750_11: +getValue('#prujezdDigiCenaDo750_11', 2.28),
    prujezdDigiCenaNad750_11: +getValue('#prujezdDigiCenaNad750_11', 1.28),
    prujezdDigiCenaDo750_10: +getValue('#prujezdDigiCenaDo750_10', 1.96),
    prujezdDigiCenaNad750_10: +getValue('#prujezdDigiCenaNad750_10', 0.96),
    barvySpecialLak50: +getValue('#barvySpecialLak50', 4),
    barvySpecialLak100: +getValue('#barvySpecialLak100', 8),
    barvySpecialBila50: +getValue('#barvySpecialBila50', 6),
    barvySpecialBila100: +getValue('#barvySpecialBila100', 12),
    barvySpecialZlata50: +getValue('#barvySpecialZlata50', 6),
    barvySpecialZlata100: +getValue('#barvySpecialZlata100', 12),

    // Laminace
    laminaceCenaMinimum: +getValue('#laminaceCenaMinimum', 200),
    laminaceLeskPrujezd10: +getValue('#laminaceLeskPrujezd10', 2.5),
    laminaceLeskPrujezd11: +getValue('#laminaceLeskPrujezd11', 5),
    laminaceMatPrujezd10: +getValue('#laminaceMatPrujezd10', 3.5),
    laminaceMatPrujezd11: +getValue('#laminaceMatPrujezd11', 7),
    laminaceStrukturaPrujezd10: +getValue('#laminaceStrukturaPrujezd10', 7.5),
    laminaceStrukturaPrujezd11: +getValue('#laminaceStrukturaPrujezd11', 15),
    laminaceSametPrujezd10: +getValue('#laminaceSametPrujezd10', 7.5),
    laminaceSametPrujezd11: +getValue('#laminaceSametPrujezd11', 15),
    laminaceLeskPrujezd10Digital: +getValue('#laminaceLeskPrujezd10Digital', 1.25),
    laminaceLeskPrujezd11Digital: +getValue('#laminaceLeskPrujezd11Digital', 2.5),
    laminaceMatPrujezd10Digital: +getValue('#laminaceMatPrujezd10Digital', 1.75),
    laminaceMatPrujezd11Digital: +getValue('#laminaceMatPrujezd11Digital', 3.5),
    laminaceStrukturaPrujezd10Digital: +getValue('#laminaceStrukturaPrujezd10Digital', 3.75),
    laminaceStrukturaPrujezd11Digital: +getValue('#laminaceStrukturaPrujezd11Digital', 7.5),
    laminaceSametPrujezd10Digital: +getValue('#laminaceSametPrujezd10Digital', 3.75),
    laminaceSametPrujezd11Digital: +getValue('#laminaceSametPrujezd11Digital', 7.5),
    laminaceKapsovaPriprava: +getValue('#laminaceKapsovaPriprava', 100),
    laminaceKapsovaA5: +getValue('#laminaceKapsovaA5', 12),
    laminaceKapsovaA4: +getValue('#laminaceKapsovaA4', 23),
    laminaceKapsovaA3: +getValue('#laminaceKapsovaA3', 33),

    // Lakování
    lakParcialPriprava10: +getValue('#lakParcialPriprava10', 1700),
    lakParcialPriprava11: +getValue('#lakParcialPriprava11', 3400),
    lakParcialPrujezd10: +getValue('#lakParcialPrujezd10', 2.5),
    lakParcialPrujezd11: +getValue('#lakParcialPrujezd11', 5),
    lakPlochaPriprava10: +getValue('#lakPlochaPriprava10', 1300),
    lakPlochaPriprava11: +getValue('#lakPlochaPriprava11', 1300),
    lakPlochaPrujezd10: +getValue('#lakPlochaPrujezd10', 2.5),
    lakPlochaPrujezd11: +getValue('#lakPlochaPrujezd11', 5),
    lakStrojPriprava10: +getValue('#lakStrojPriprava10', 250),
    lakStrojPriprava11: +getValue('#lakStrojPriprava11', 250),
    lakStrojPrujezd10: +getValue('#lakStrojPrujezd10', 0.2),
    lakStrojPrujezd11: +getValue('#lakStrojPrujezd11', 0.4),

    // Výsek / předsek / ražba / perforace / lepení
    vysekPriprava: +getValue('#vysekPriprava', 700),
    vysekCena: +getValue('#vysekCena', 500),
    vysekRozmeryMinX: +getValue('#vysekRozmeryMinX', 0),
    vysekRozmeryMinY: +getValue('#vysekRozmeryMinY', 0),
    vysekRozmeryMaxX: +getValue('#vysekRozmeryMaxX', 500),
    vysekRozmeryMaxY: +getValue('#vysekRozmeryMaxY', 700),
    predsekPriprava: +getValue('#predsekPriprava', 700),
    predsekCena: +getValue('#predsekCena', 500),
    predsekRozmeryMinX: +getValue('#predsekRozmeryMinX', 0),
    predsekRozmeryMinY: +getValue('#predsekRozmeryMinY', 0),
    predsekRozmeryMaxX: +getValue('#predsekRozmeryMaxX', 500),
    predsekRozmeryMaxY: +getValue('#predsekRozmeryMaxY', 700),
    razbaPriprava: +getValue('#razbaPriprava', 1000),
    razbaCena: +getValue('#razbaCena', 800),
    razbaRozmeryMinX: +getValue('#razbaRozmeryMinX', 0),
    razbaRozmeryMinY: +getValue('#razbaRozmeryMinY', 0),
    razbaRozmeryMaxX: +getValue('#razbaRozmeryMaxX', 350),
    razbaRozmeryMaxY: +getValue('#razbaRozmeryMaxY', 500),
    perforacePriprava: +getValue('#perforacePriprava', 100),
    perforaceCenaZaKus: +getValue('#perforaceCenaZaKus', 0.1),
    cenaZaLepeniKrabicka: +getValue('#cenaZaLepeniKrabicka', 2),
    lepeniKrabickaPriprava: +getValue('#lepeniKrabickaPriprava', 200),

    // Rozměrové limity falcování / bigování
    falcovaniRozmeryMinX: +getValue('#falcovaniRozmeryMinX', 148),
    falcovaniRozmeryMinY: +getValue('#falcovaniRozmeryMinY', 40),
    falcovaniRozmeryMaxZ: +getValue('#falcovaniRozmeryMaxZ', 353),
    falcovaniRozmeryMaxY: +getValue('#falcovaniRozmeryMaxY', 500),
    bigovaniRozmeryMinX: +getValue('#bigovaniRozmeryMinX', 148),
    bigovaniRozmeryMinY: +getValue('#bigovaniRozmeryMinY', 40),
    bigovaniRozmeryMaxX: +getValue('#bigovaniRozmeryMaxX', 353),
    bigovaniRozmeryMaxY: +getValue('#bigovaniRozmeryMaxY', 500),


    // Doprava
    dopravaOsobni: +getValue('#dopravaOsobni', 0),
    dopravaDpd: +getValue('#dopravaDpd', 100),
    dopravaDpdDo1Kg: +getValue('#dopravaDpdDo1Kg', 133),
    dopravaDpdDo2Kg: +getValue('#dopravaDpdDo2Kg', 155),
    dopravaDpdDo3Kg: +getValue('#dopravaDpdDo3Kg', 157),
    dopravaDpdDo4Kg: +getValue('#dopravaDpdDo4Kg', 161),
    dopravaDpdDo5Kg: +getValue('#dopravaDpdDo5Kg', 163),
    dopravaDpdDo6Kg: +getValue('#dopravaDpdDo6Kg', 170),
    dopravaDpdDo7Kg: +getValue('#dopravaDpdDo7Kg', 178),
    dopravaTria: +getValue('#dopravaTria', 0),
    dopravaZdarmaOd: +getValue('#dopravaZdarmaOd', 3000),

    // Lepené bloky – geometrie přebalu
    blokPresahMin: +getValue('#blokPresahMin', 20),
    blokPresahMax: +getValue('#blokPresahMax', 40),
    blokTloustkaKoeficient: +getValue('#blokTloustkaKoeficient', 1.2),

    // Falcování – ceny podle typu lomu
    falcovaniHalfFoldPriprava: +getValue('#falcovaniHalfFoldPriprava', 100),
    falcovaniHalfFoldCena: +getValue('#falcovaniHalfFoldCena', 0.1),
    falcovaniCrossFoldPriprava: +getValue('#falcovaniCrossFoldPriprava', 200),
    falcovaniCrossFoldCena: +getValue('#falcovaniCrossFoldCena', 0.2),
    falcovaniUFoldPriprava: +getValue('#falcovaniUFoldPriprava', 100),
    falcovaniUFoldCena: +getValue('#falcovaniUFoldCena', 0.2),
    falcovaniZFoldPriprava: +getValue('#falcovaniZFoldPriprava', 100),
    falcovaniZFoldCena: +getValue('#falcovaniZFoldCena', 0.2),
    falcovaniDoubleHalfFoldPriprava: +getValue('#falcovaniDoubleHalfFoldPriprava', 100),
    falcovaniDoubleHalfFoldCena: +getValue('#falcovaniDoubleHalfFoldCena', 0.2),
    falcovaniRollFoldPriprava: +getValue('#falcovaniRollFoldPriprava', 100),
    falcovaniRollFoldCena: +getValue('#falcovaniRollFoldCena', 0.7),
    falcovaniHarmoniumFold3Priprava: +getValue('#falcovaniHarmoniumFold3Priprava', 100),
    falcovaniHarmoniumFold3Cena: +getValue('#falcovaniHarmoniumFold3Cena', 0.3),
    falcovaniHarmoniumFold4Priprava: +getValue('#falcovaniHarmoniumFold4Priprava', 100),
    falcovaniHarmoniumFold4Cena: +getValue('#falcovaniHarmoniumFold4Cena', 0.4),
    falcovaniHarmoniumFold5Priprava: +getValue('#falcovaniHarmoniumFold5Priprava', 100),
    falcovaniHarmoniumFold5Cena: +getValue('#falcovaniHarmoniumFold5Cena', 1),
    falcovaniGateFoldPriprava: +getValue('#falcovaniGateFoldPriprava', 100),
    falcovaniGateFoldCena: +getValue('#falcovaniGateFoldCena', 0.6),
    falcovaniDoubleGateFoldPriprava: +getValue('#falcovaniDoubleGateFoldPriprava', 100),
    falcovaniDoubleGateFoldCena: +getValue('#falcovaniDoubleGateFoldCena', 0.7),

    // Bigování – ceny podle typu bigu
    bigovaniHalfFoldPriprava: +getValue('#bigovaniHalfFoldPriprava', 100),
    bigovaniHalfFoldCena: +getValue('#bigovaniHalfFoldCena', 0.4),
    bigovaniCrossFoldPriprava: +getValue('#bigovaniCrossFoldPriprava', 100),
    bigovaniCrossFoldCena: +getValue('#bigovaniCrossFoldCena', 0.8),
    bigovaniUFoldPriprava: +getValue('#bigovaniUFoldPriprava', 100),
    bigovaniUFoldCena: +getValue('#bigovaniUFoldCena', 0.8),
    bigovaniZFoldPriprava: +getValue('#bigovaniZFoldPriprava', 100),
    bigovaniZFoldCena: +getValue('#bigovaniZFoldCena', 0.8),
    bigovaniDoubleHalfFoldPriprava: +getValue('#bigovaniDoubleHalfFoldPriprava', 100),
    bigovaniDoubleHalfFoldCena: +getValue('#bigovaniDoubleHalfFoldCena', 1.2),
    bigovaniRollFoldPriprava: +getValue('#bigovaniRollFoldPriprava', 100),
    bigovaniRollFoldCena: +getValue('#bigovaniRollFoldCena', 1.2),
    bigovaniHarmoniumFold3Priprava: +getValue('#bigovaniHarmoniumFold3Priprava', 100),
    bigovaniHarmoniumFold3Cena: +getValue('#bigovaniHarmoniumFold3Cena', 1.2),
    bigovaniHarmoniumFold4Priprava: +getValue('#bigovaniHarmoniumFold4Priprava', 100),
    bigovaniHarmoniumFold4Cena: +getValue('#bigovaniHarmoniumFold4Cena', 1.6),
    bigovaniHarmoniumFold5Priprava: +getValue('#bigovaniHarmoniumFold5Priprava', 100),
    bigovaniHarmoniumFold5Cena: +getValue('#bigovaniHarmoniumFold5Cena', 1.6),
    bigovaniGateFoldPriprava: +getValue('#bigovaniGateFoldPriprava', 100),
    bigovaniGateFoldCena: +getValue('#bigovaniGateFoldCena', 0.8),
    bigovaniDoubleGateFoldPriprava: +getValue('#bigovaniDoubleGateFoldPriprava', 100),
    bigovaniDoubleGateFoldCena: +getValue('#bigovaniDoubleGateFoldCena', 1.2),
    bigovaniSpine4Priprava: +getValue('#bigovaniSpine4Priprava', 100),
    bigovaniSpine4Cena: +getValue('#bigovaniSpine4Cena', 1.6),

    // Vazba - V1 šitá skobou
    vazbaV1StrojniPriprava: +getValue('#vazbaV1StrojniPriprava', 200),
    vazbaV1StrojniCenaZaKus: +getValue('#vazbaV1StrojniCenaZaKus', 0.6),
    vazbaV1RucniPriprava: +getValue('#vazbaV1RucniPriprava', 100),
    vazbaV1RucniCenaZaKus: +getValue('#vazbaV1RucniCenaZaKus', 1.6),
    vazbaV1RucniCenaZaStranu: +getValue('#vazbaV1RucniCenaZaStranu', 0.3),
    // Vazba - Kroužková twin wire (cenové stupně podle rozměru)
    vazbaKrouzekBlokA7Priprava: +getValue('#vazbaKrouzekBlokA7Priprava', 200),
    vazbaKrouzekBlokA7CenaZaKus: +getValue('#vazbaKrouzekBlokA7CenaZaKus', 10.80),
    vazbaKrouzekBlokA6Priprava: +getValue('#vazbaKrouzekBlokA6Priprava', 200),
    vazbaKrouzekBlokA6CenaZaKus: +getValue('#vazbaKrouzekBlokA6CenaZaKus', 11.80),
    vazbaKrouzekBlokA5Priprava: +getValue('#vazbaKrouzekBlokA5Priprava', 200),
    vazbaKrouzekBlokA5CenaZaKus: +getValue('#vazbaKrouzekBlokA5CenaZaKus', 12.30),
    vazbaKrouzekBlokA4Priprava: +getValue('#vazbaKrouzekBlokA4Priprava', 200),
    vazbaKrouzekBlokA4CenaZaKus: +getValue('#vazbaKrouzekBlokA4CenaZaKus', 12.80),
    vazbaKrouzekBlokA3Priprava: +getValue('#vazbaKrouzekBlokA3Priprava', 200),
    vazbaKrouzekBlokA3CenaZaKus: +getValue('#vazbaKrouzekBlokA3CenaZaKus', 14.00),
    // Vazba - Kroužková s háčkem na pověšení
    vazbaKrouzekKalendarPriprava: +getValue('#vazbaKrouzekKalendarPriprava', 200),
    vazbaKrouzekKalendarCenaZaKus: +getValue('#vazbaKrouzekKalendarCenaZaKus', 14.00),
    // Vazba - V2 PUR (marginální ceník: sazba klesá kusStart → kusKonec)
    vazbaV2PurPriprava: +getValue('#vazbaV2PurPriprava', 400),
    vazbaV2PurKusStart: +getValue('#vazbaV2PurKusStart', 14),
    vazbaV2PurKusKonec: +getValue('#vazbaV2PurKusKonec', 8),
    vazbaV2PurPrah: +getValue('#vazbaV2PurPrah', 200),
    vazbaV2PurUtlum: +getValue('#vazbaV2PurUtlum', 300),

    // ============================================================
    // HODINOVÁ KALKULACE (interní debug)
    // Poznámka: hourlyEnabled se UŽ NEUKLÁDÁ do globálních nastavení
    // Je to user-specific nastavení v session (viz profil uživatele)
    // ============================================================
    // Tiskové stroje
    hourlyOfsetCost: +getValue('#hourlyOfsetCost', 2500),
    hourlyOfsetSetup: +getValue('#hourlyOfsetSetup', 0.5),
    hourlyOfsetSpeed: +getValue('#hourlyOfsetSpeed', 8000),
    hourlyOfsetColorChange: +getValue('#hourlyOfsetColorChange', 0.15),
    hourlyDigitalCost: +getValue('#hourlyDigitalCost', 1500),
    hourlyDigitalSetup: +getValue('#hourlyDigitalSetup', 0.1),
    hourlyDigitalSpeed: +getValue('#hourlyDigitalSpeed', 3000),
    // Dokončovací operace
    hourlyLaminaceCost: +getValue('#hourlyLaminaceCost', 800),
    hourlyLaminaceSetup: +getValue('#hourlyLaminaceSetup', 0.25),
    hourlyLaminaceSpeed: +getValue('#hourlyLaminaceSpeed', 2000),
    hourlyLakovaniCost: +getValue('#hourlyLakovaniCost', 1200),
    hourlyLakovaniSetup: +getValue('#hourlyLakovaniSetup', 0.33),
    hourlyLakovaniSpeed: +getValue('#hourlyLakovaniSpeed', 1500),
    hourlyFalcovaniCost: +getValue('#hourlyFalcovaniCost', 600),
    hourlyFalcovaniSetup: +getValue('#hourlyFalcovaniSetup', 0.17),
    hourlyFalcovaniSpeed: +getValue('#hourlyFalcovaniSpeed', 10000),
    hourlyBigovaniCost: +getValue('#hourlyBigovaniCost', 500),
    hourlyBigovaniSetup: +getValue('#hourlyBigovaniSetup', 0.17),
    hourlyBigovaniSpeed: +getValue('#hourlyBigovaniSpeed', 5000),
    hourlyVysekCost: +getValue('#hourlyVysekCost', 1000),
    hourlyVysekSetup: +getValue('#hourlyVysekSetup', 0.5),
    hourlyVysekSpeed: +getValue('#hourlyVysekSpeed', 3000),
    hourlyRazbaCost: +getValue('#hourlyRazbaCost', 1200),
    hourlyRazbaSetup: +getValue('#hourlyRazbaSetup', 0.5),
    hourlyRazbaSpeed: +getValue('#hourlyRazbaSpeed', 2000),
    vrtaniPriprava: +getValue('#vrtaniPriprava', 160),
    vrtaniEuroBlokPriprava: +getValue('#vrtaniEuroBlokPriprava', 100),
    hourlyVrtaniCost: +getValue('#hourlyVrtaniCost', 400),
    hourlyVrtaniSetup: +getValue('#hourlyVrtaniSetup', 0.17),
    hourlyVrtaniSpeed: +getValue('#hourlyVrtaniSpeed', 8000),
    hourlyVazbaCost: +getValue('#hourlyVazbaCost', 800),
    hourlyVazbaSetup: +getValue('#hourlyVazbaSetup', 0.33),
    hourlyVazbaSpeed: +getValue('#hourlyVazbaSpeed', 500),
    hourlyLepeniCost: +getValue('#hourlyLepeniCost', 300),
    hourlyLepeniSetup: +getValue('#hourlyLepeniSetup', 0.17),
    hourlyLepeniSpeed: +getValue('#hourlyLepeniSpeed', 1000),
    // Režijní náklady
    hourlyOverheadCoefficient: +getValue('#hourlyOverheadCoefficient', 1.15),
    hourlyMinimumTime: +getValue('#hourlyMinimumTime', 0.25)
  };
  
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(settings)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
              debugLog('Nastavení uložena:', result.data);
      
      // Aktualizovat informace o posledním uložení
      const settingsLastSavedElement = document.getElementById('settings-last-saved-info');
      if (settingsLastSavedElement && result.data.lastUpdatedBy) {
        const lastUpdated = new Date(result.data.lastUpdated);
        const userName = result.data.lastUpdatedBy;
        const storageType = result.data.storageType;
        const storageText = storageType === 'redis' ? 'cloud databáze' : 'lokální soubor';
        settingsLastSavedElement.textContent = `Poslední úpravu provedl uživatel ${userName} ${lastUpdated.toLocaleString('cs-CZ')} (${storageText})`;
      }
      
      // Zobrazit notifikaci pouze pokud je aktivní sekce nastavení
      if (typeof sectionManager !== 'undefined' && sectionManager.currentSection === 'nastaveni') {
        showNotification('Nastavení uložena', 'success');
      }
    } else {
      throw new Error(result.error || 'Neznámá chyba');
    }
  } catch (error) {
    console.error('Chyba při ukládání nastavení:', error);
    if (typeof sectionManager !== 'undefined' && sectionManager.currentSection === 'nastaveni') {
      showNotification('Chyba při ukládání nastavení: ' + error.message, 'danger');
    }
  }
}

// Funkce pro načtení nastavení z API
async function loadSettings() {
  try {
    // GET /api/settings vyžaduje přihlášení (cookie) – volá se až po unlockApp()
    const response = await fetch(`${API_BASE_URL}/settings`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      const settings = result.data.settings;
      debugLog('Načítám nastavení z API:', settings);
      
      // Aplikuj nastavení do formuláře
      let appliedCount = 0;
      Object.keys(settings).forEach(key => {
        // Přeskoč 'technologie' - to je lokální nastavení v localStorage
        if (key === 'technologie') {
          return;
        }
        
        const element = document.getElementById(key);
        if (element) {
          if (element.type === 'checkbox') {
            element.checked = settings[key];
          } else {
            element.value = settings[key];
          }
          appliedCount++;
        }
        // Tiché ignorování chybějících elementů - normální stav
      });
      
      debugLog(`Načteno ${appliedCount} nastavení do formuláře`);

      // Přegenerovat možnosti dopravy podle načtených cen
      updateDopravaOptions();
      
      // Aktualizovat informace o posledním uložení při načtení
      const settingsLastSavedElement = document.getElementById('settings-last-saved-info');
      if (settingsLastSavedElement && result.data.lastUpdated) {
        const lastUpdated = new Date(result.data.lastUpdated);
        const userName = result.data.lastUpdatedBy || 'neznámý';
        const storageType = result.data.storageType || 'file';
        const storageText = storageType === 'redis' ? 'cloud databáze' : 'lokální soubor';
        settingsLastSavedElement.textContent = `Poslední úpravu provedl uživatel ${userName} ${lastUpdated.toLocaleString('cs-CZ')} (${storageText})`;
      }
    } else {
      throw new Error(result.error || 'Neznámá chyba');
    }
  } catch (error) {
    console.error('Chyba při načítání nastavení:', error);
    // Tiché ignorování chyby pro nepřihlášené uživatele - použijí se výchozí hodnoty
    debugLog('Nastavení nebyla načtena, používají se výchozí hodnoty');
    // Fallback informace do infoboxu pro případ, že API selže
    const settingsLastSavedElement = document.getElementById('settings-last-saved-info');
    if (settingsLastSavedElement) {
      // Zachováme původní text pokud existuje, jinak nastavíme chybovou zprávu
      const currentText = settingsLastSavedElement.textContent.trim();
      if (!currentText || currentText === '(načítám z API...)' || currentText.includes('chyba')) {
        settingsLastSavedElement.textContent = '(chyba načtení z API)';
      }
      // Jinak ponecháme původní text - neztratíme informaci o centrálním nastavení
    }
  }
}

// Funkce pro reset nastavení na výchozí hodnoty
async function resetSettings() {
  if (confirm('Opravdu chcete resetovat všechna nastavení na výchozí hodnoty?')) {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authManager.getAuthHeaders()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        debugLog('Nastavení resetována:', result.data);
        
        // Resetovat lokální technologii tisku na 'auto'
        saveLocalTechnologie('auto');
        const technologieSelect = document.getElementById('technologie');
        if (technologieSelect) {
          technologieSelect.value = 'auto';
        }
        
        // Znovu načíst nastavení do formuláře
        await loadSettings();
        
        // Spustit přepočet
        pocitej();
        
        showNotification('Nastavení resetována na výchozí hodnoty', 'info');
      } else {
        throw new Error(result.error || 'Neznámá chyba');
      }
    } catch (error) {
      console.error('Chyba při resetování nastavení:', error);
      showNotification('Chyba při resetování nastavení: ' + error.message, 'danger');
    }
  }
}



// Funkce pro export nastavení
async function exportSettings() {
  try {
    const response = await fetch(`${API_BASE_URL}/settings/export`, {
      headers: {
        ...authManager.getAuthHeaders()
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const settings = await response.json();
    
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(settings, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "tria-calculator-settings.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    
    showNotification('Nastavení exportována', 'success');
  } catch (error) {
    console.error('Chyba při exportu nastavení:', error);
    showNotification('Chyba při exportu nastavení: ' + error.message, 'danger');
  }
}

// Funkce pro import nastavení
function importSettings() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json';
  
  input.onchange = function(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async function(e) {
      try {
        const settings = JSON.parse(e.target.result);
        
        // Poslat na API
        const response = await fetch(`${API_BASE_URL}/settings/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(settings)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          // Znovu načíst nastavení do formuláře
          await loadSettings();
          
          // Spustit přepočet
          pocitej();
          
          showNotification('Nastavení byla úspěšně importována', 'success');
        } else {
          throw new Error(result.error || 'Neznámá chyba');
        }
      } catch (error) {
        console.error('Chyba při importu nastavení:', error);
        showNotification('Chyba při importu nastavení: ' + error.message, 'danger');
      }
    };
    reader.readAsText(file);
  };
  
  input.click();
}

// ============================================================================
// LOKÁLNÍ NASTAVENÍ TECHNOLOGIE TISKU (user-specific, sessionStorage)
// Nastavení platí pouze pro aktuální session (do zavření prohlížeče).
// Pro nepřihlášené uživatele je výchozí hodnota 'auto'.
// ============================================================================

// Funkce pro načtení technologie z sessionStorage
function loadLocalTechnologie() {
  const saved = sessionStorage.getItem('technologie');
  return saved || 'auto'; // Výchozí hodnota je 'auto'
}

// Funkce pro uložení technologie do sessionStorage
function saveLocalTechnologie(value) {
  sessionStorage.setItem('technologie', value);
}

// Inicializace event listeneru pro technologii při změně
function initTechnologieListener() {
  const technologieSelect = document.getElementById('technologie');
  if (technologieSelect) {
    // Načíst uloženou hodnotu při načtení stránky
    const savedValue = loadLocalTechnologie();
    technologieSelect.value = savedValue;
    
    // Event listener pro ukládání při změně
    technologieSelect.addEventListener('change', (e) => {
      saveLocalTechnologie(e.target.value);
      // Přepočítat kalkulaci
      pocitej();
    });
  }
}

// Exportuji funkce globálně
window.resetSettings = resetSettings;
window.saveSettings = saveSettings;
window.loadSettings = loadSettings;
window.exportSettings = exportSettings;
window.importSettings = importSettings;
window.showNotification = showNotification;
window.loadLocalTechnologie = loadLocalTechnologie;
window.saveLocalTechnologie = saveLocalTechnologie;

// Funkce pro renderování materiálů do select elementu
function renderMaterialsToSelect(materials) {
  const select = document.getElementById('material');
  if (!select) return;
  
  // Zjistím aktuálně vybranou hodnotu
  const selectedValue = select.value;
  
  // Vyčistím select
  select.innerHTML = '';
  
  // Kategorie a jejich názvy
  const categoryNames = {
    'ofset': 'Ofsetový papír',
    'laserova': 'Do laserové tiskárny',
    'krida-leskla': 'Lesklá křída',
    'krida-matna': 'Matná křída',
    'recyklovane': 'Recyklované a ekologické papíry',
    'lepenky': 'Lepenky',
    'samolepici': 'Samolepící papíry',
    'kartony': 'Kartóny',
    'specialni': 'Speciální grafické papíry'
  };
  
  // Pořadí kategorií
  const categoryOrder = ['ofset', 'laserova', 'krida-leskla', 'krida-matna', 'recyklovane', 'lepenky', 'samolepici', 'kartony', 'specialni'];
  
  // Vytvořím optgroup pro každou kategorii
  categoryOrder.forEach(categoryId => {
    const categoryMaterials = materials[categoryId];
    if (!categoryMaterials || categoryMaterials.length === 0) return;
    
    const optgroup = document.createElement('optgroup');
    optgroup.label = categoryNames[categoryId];
    
    categoryMaterials.forEach(material => {
      const option = document.createElement('option');
      option.value = material.price;
      option.textContent = material.name;
      option.setAttribute('data-obratka', material.canFlip ? '1' : '0');
      option.setAttribute('data-gramaz', material.weight);
      option.setAttribute('data-vyska', material.width);  // Pozor: width je výška archu
      option.setAttribute('data-sirka', material.height); // Pozor: height je šířka archu
      
      // Pokud je to stejná hodnota jako předchozí selected, označím jako selected
      if (selectedValue && selectedValue === material.price.toString()) {
        option.selected = true;
      }
      
      optgroup.appendChild(option);
    });
    
    select.appendChild(optgroup);
  });
  
  // Přidám možnost "Vlastní materiál" na konec
  const customOption = document.createElement('option');
  customOption.value = 'vlastni';
  customOption.textContent = 'Vlastní materiál';
  customOption.setAttribute('data-obratka', '1');
  customOption.setAttribute('data-gramaz', '200');
  customOption.setAttribute('data-vyska', '650');
  customOption.setAttribute('data-sirka', '480');
  
  select.appendChild(customOption);
  
  // Pokud nebyla žádná hodnota vybrána, vyberu výchozí (Ofset 80 g/m²)
  if (!selectedValue) {
    const defaultOption = select.querySelector('option[value="1.43"]');
    if (defaultOption) {
      defaultOption.selected = true;
    }
  }

  // Synchronizovat materiály do vnitřního bloku selectu (pokud existuje)
  syncVnitrniMaterialSelect();
  // Synchronizovat materiály do podložky selectu (pokud existuje)
  syncPodlozkaMaterialSelect();
}

// Základní objekty pro zadání
var produkt = {
  nazev: '',
  technologieTisku: 'auto',
  dtp: 0,
  pocetKusu: 0,
  barevnost: '4/4',
  primeBarvy: 0,
  okrajKolemProduktu: 3,
  okrajKolemArchu: 7,
  okrajKolemArchuDigital: 7,
  rozmer: {
    format: 'A4',
    sirka: 210,
    vyska: 297
  },
  material: {
    nazev: 'Matná křída',
    cena: 0,
    gramaz: 115,
    // false = jednostranně natíraný papír → nelze tisknout na obrátku
    canFlip: true,
    rozmer: {
      sirka: 500,
      vyska: 707,
    },
  },
  laminace: { typ: '0' },
  lak: { typ: '0' },
  falcovani: { typ: '0' },
  bigovani: { typ: '0' },
  vysek: { option: 0, pozicNaVysek: 0, cenaRazniceVyseku: 0 },
  predsek: { option: 0, pozicNaPredsek: 0, cenaRaznicePredseku: 0 },
  razba: { option: 0, pozicNaRazbu: 0, cenaStockuRazby: 0 },
  vrtani: { pocetVrtu: 0 },
  perforace: { pocetPerforaci: 0 },
  lepeni: { typ: '0', pocetLepeni: 0 },
  vazba: { typ: '0', nazev: '', cena: 0, text: '', orientace: 'vysku', orientaceText: 'Na delší straně', pocetStran: 0, jinaObalka: false, vnitrniBlok: null, podlozka: null },
  barvyDigital: { typ: 0 }
};

var objednavka = {
  doprava: {
    cena: 0,
    text: ''
  }
};

var zakaznik = {
  kategorie: 'velko'
};

var nastaveni = {
  koeficientCena: 1.2,
  dph: 1.21,
  stroje: {
    ofset: {
      horniOkraj: 17,
      dolniOkraj: 15,
      pravyOkraj: 10,
      levyOkraj: 10,
      barvaCena: 500,
      mytiCena: 250,
      prujezdCena: 0.25,
      obratkaCena: 350,
      obratka: true
    },
    digital: {
      horniOkraj: 0,
      dolniOkraj: 0,
      pravyOkraj: 0,
      levyOkraj: 0,
      pripravaCernobile: 200,
      pripravaBarva: 200,
      pripravaBarvaSpecial: 0,
      prujezdDigiCenaDo750_44: 5.74,
      prujezdDigiCenaNad750_44: 3.3,
      prujezdDigiCenaDo750_40: 4.74,
      prujezdDigiCenaNad750_40: 2.3,
      prujezdDigiCenaDo750_11: 2.28,
      prujezdDigiCenaNad750_11: 1.28,
      prujezdDigiCenaDo750_10: 1.96,
      prujezdDigiCenaNad750_10: 0.96,
      barvySpecialLak50: 4,
      barvySpecialLak100: 8,
      barvySpecialBila50: 6,
      barvySpecialBila100: 12,
      barvySpecialZlata50: 6,
      barvySpecialZlata100: 12,
    },
  },
  vrtani: {
    priprava: 160,
    euroBlokPriprava: 100
  }
};

// Funkce pro získání hodnot z formuláře
function getN(e) {
  const element = document.querySelector(e);
  return element;
}

// Bezpečná funkce pro získání hodnoty
function getValue(selector, defaultValue = 0) {
  const element = getN(selector);
  if (!element) {
    debugLog(`Element ${selector} not found, using default value: ${defaultValue}`);
    return defaultValue;
  }
  return element.value;
}

// Bezpečná funkce pro získání checked stavu
function getChecked(selector, defaultValue = false) {
  const element = getN(selector);
  if (!element) {
    debugLog(`Element ${selector} not found, using default value: ${defaultValue}`);
    return defaultValue;
  }
  return element.checked;
}

// Bezpečná funkce pro získání hodnoty vybraného option
function getSelectedValue(selector, defaultValue = '') {
  const element = getN(selector + ' option:checked');
  if (!element) {
    debugLog(`Selected option in ${selector} not found, using default value: ${defaultValue}`);
    return defaultValue;
  }
  return element.value;
}

// Bezpečná funkce pro získání textu vybraného option
function getSelectedText(selector, defaultValue = '') {
  const element = getN(selector + ' option:checked');
  if (!element) {
    debugLog(`Selected option in ${selector} not found, using default value: ${defaultValue}`);
    return defaultValue;
  }
  return element.text;
}

// Bezpečná funkce pro získání atributu vybraného option
function getSelectedAttribute(selector, attr, defaultValue = '') {
  const element = getN(selector + ' option:checked');
  if (!element) {
    debugLog(`Selected option in ${selector} not found, using default value: ${defaultValue}`);
    return defaultValue;
  }
  return element.getAttribute(attr) || defaultValue;
}

function gatherForm() {
  // Načtení nastavení
  nastaveni.stroje.ofset.barvaCena = +getValue('#barvaCena', 500);
  nastaveni.stroje.ofset.mytiCena = +getValue('#mytiCena', 250);
  nastaveni.stroje.ofset.prujezdCena = +getValue('#prujezdOfsetCena', 0.25);
  nastaveni.stroje.ofset.obratkaCena = +getValue('#obratkaCena', 350);
  nastaveni.stroje.ofset.soubehSleva = +getValue('#ofsetSoubehSleva', 0.8);
  nastaveni.stroje.ofset.horniOkraj = +getValue('#horniOkraj', 17);
  nastaveni.stroje.ofset.dolniOkraj = +getValue('#dolniOkraj', 15);
  nastaveni.stroje.ofset.pravyOkraj = +getValue('#pravyOkraj', 10);
  nastaveni.stroje.ofset.levyOkraj = +getValue('#levyOkraj', 10);
  nastaveni.koeficientCena = +getValue('#koeficientCena', 1.2);
  nastaveni.dph = +getValue('#dph', 1.21);
  
  // Načtení digitálních nastavení
  nastaveni.stroje.digital.horniOkraj = +getValue('#horniOkrajDigital', 0);
  nastaveni.stroje.digital.dolniOkraj = +getValue('#dolniOkrajDigital', 0);
  nastaveni.stroje.digital.pravyOkraj = +getValue('#pravyOkrajDigital', 0);
  nastaveni.stroje.digital.levyOkraj = +getValue('#levyOkrajDigital', 0);
  nastaveni.stroje.digital.pripravaCernobile = +getValue('#pripravaCernobile', 200);
  nastaveni.stroje.digital.pripravaBarva = +getValue('#pripravaBarva', 200);
  nastaveni.stroje.digital.pripravaBarvaSpecial = +getValue('#pripravaBarvaSpecial', 0);
  nastaveni.stroje.digital.prujezdDigiCenaDo750_44 = +getValue('#prujezdDigiCenaDo750_44', 5.74);
  nastaveni.stroje.digital.prujezdDigiCenaNad750_44 = +getValue('#prujezdDigiCenaNad750_44', 3.3);
  nastaveni.stroje.digital.prujezdDigiCenaDo750_40 = +getValue('#prujezdDigiCenaDo750_40', 4.74);
  nastaveni.stroje.digital.prujezdDigiCenaNad750_40 = +getValue('#prujezdDigiCenaNad750_40', 2.3);
  nastaveni.stroje.digital.prujezdDigiCenaDo750_11 = +getValue('#prujezdDigiCenaDo750_11', 2.28);
  nastaveni.stroje.digital.prujezdDigiCenaNad750_11 = +getValue('#prujezdDigiCenaNad750_11', 1.28);
  nastaveni.stroje.digital.prujezdDigiCenaDo750_10 = +getValue('#prujezdDigiCenaDo750_10', 1.96);
  nastaveni.stroje.digital.prujezdDigiCenaNad750_10 = +getValue('#prujezdDigiCenaNad750_10', 0.96);
  nastaveni.stroje.digital.barvySpecialLak50 = +getValue('#barvySpecialLak50', 4);
  nastaveni.stroje.digital.barvySpecialLak100 = +getValue('#barvySpecialLak100', 8);
  nastaveni.stroje.digital.barvySpecialBila50 = +getValue('#barvySpecialBila50', 6);
  nastaveni.stroje.digital.barvySpecialBila100 = +getValue('#barvySpecialBila100', 12);
  nastaveni.stroje.digital.barvySpecialZlata50 = +getValue('#barvySpecialZlata50', 6);
  nastaveni.stroje.digital.barvySpecialZlata100 = +getValue('#barvySpecialZlata100', 12);

  // Načtení nastavení vrtání
  nastaveni.vrtani.priprava = +getValue('#vrtaniPriprava', 160);
  nastaveni.vrtani.euroBlokPriprava = +getValue('#vrtaniEuroBlokPriprava', 100);

  // Načtení nastavení vazby
  nastaveni.vazba = nastaveni.vazba || {};
  nastaveni.vazba['V1-strojni'] = {
    priprava: +getValue('#vazbaV1StrojniPriprava', 200),
    cenaZaKus: +getValue('#vazbaV1StrojniCenaZaKus', 0.6),
  };
  nastaveni.vazba['V1-rucni'] = {
    priprava: +getValue('#vazbaV1RucniPriprava', 100),
    cenaZaKus: +getValue('#vazbaV1RucniCenaZaKus', 1.6),
    cenaZaStranu: +getValue('#vazbaV1RucniCenaZaStranu', 0.3),
  };
  // Kroužková twin wire - cenové stupně
  nastaveni.vazba['krouzek-blok-A7'] = {
    priprava: +getValue('#vazbaKrouzekBlokA7Priprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekBlokA7CenaZaKus', 10.80),
  };
  nastaveni.vazba['krouzek-blok-A6'] = {
    priprava: +getValue('#vazbaKrouzekBlokA6Priprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekBlokA6CenaZaKus', 11.80),
  };
  nastaveni.vazba['krouzek-blok-A5'] = {
    priprava: +getValue('#vazbaKrouzekBlokA5Priprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekBlokA5CenaZaKus', 12.30),
  };
  nastaveni.vazba['krouzek-blok-A4'] = {
    priprava: +getValue('#vazbaKrouzekBlokA4Priprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekBlokA4CenaZaKus', 12.80),
  };
  nastaveni.vazba['krouzek-blok-A3'] = {
    priprava: +getValue('#vazbaKrouzekBlokA3Priprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekBlokA3CenaZaKus', 14.00),
  };
  // Kroužková s háčkem na pověšení
  nastaveni.vazba['krouzek-kalendar'] = {
    priprava: +getValue('#vazbaKrouzekKalendarPriprava', 200),
    cenaZaKus: +getValue('#vazbaKrouzekKalendarCenaZaKus', 14.00),
  };
  // V2 PUR – marginální ceník (sazba plynule klesá kusStart → kusKonec)
  nastaveni.vazba['V2-PUR'] = {
    priprava: +getValue('#vazbaV2PurPriprava', 400),
    kusStart: +getValue('#vazbaV2PurKusStart', 14),
    kusKonec: +getValue('#vazbaV2PurKusKonec', 8),
    prah: +getValue('#vazbaV2PurPrah', 200),
    utlum: +getValue('#vazbaV2PurUtlum', 300),
  };

  // Načtení produktových nastavení
  produkt.nazev = getValue('#nazev', '');
  produkt.dtp = +getValue('#dtp', 0);
  produkt.pocetKusu = +getValue('#pocetKusu', 1000);
  produkt.pocetDruhu = +getValue('#pocetDruhu', 1);
  // Motivy s vlastními náklady – při druzích >= 2 jsou zdrojem pravdy řádky panelu
  produkt.motivy = produkt.pocetDruhu > 1 ? getMotivyRows().filter((m) => m.pocetKusu > 0) : null;
  // Načíst technologii z lokálního úložiště (sessionStorage), nikoliv z centrálního nastavení
  produkt.technologieTisku = loadLocalTechnologie();
  nastaveni.stroje.ofset.obratka = getChecked('#obratka', true);
  zakaznik.kategorie = getSelectedValue('#typCeny', 'velko');
  produkt.barevnost = getSelectedValue('#pocetBarev', '4/4');
  produkt.primeBarvy = +getValue('#primeBarvy', 0);
  produkt.barvyDigital.typ = getSelectedValue('#barvyDigital', 0);
  produkt.okrajKolemProduktu = +getValue('#okrajKolemProduktu', 3);
  produkt.okrajKolemArchu = +getValue('#okrajKolemArchu', 7);
  produkt.okrajKolemArchuDigital = +getValue('#okrajKolemArchuDigital', 7);

  // Načtení rozměrů
  produkt.rozmer.format = getSelectedText('#produktRozmerFormat', 'A4');
  if (produkt.rozmer.format === 'Vlastní rozměr') {
    produkt.rozmer.sirka = +getValue('#produktRozmerSirka', 210);
    produkt.rozmer.vyska = +getValue('#produktRozmerVyska', 297);
    produkt.rozmer.format = produkt.rozmer.sirka + '×' + produkt.rozmer.vyska + ' mm';
  } else {
    produkt.rozmer.sirka = +getSelectedAttribute('#produktRozmerFormat', 'data-sirka', '210');
    produkt.rozmer.vyska = +getSelectedAttribute('#produktRozmerFormat', 'data-vyska', '297');
    const sirkaEl = getN('#produktRozmerSirka');
    const vyskaEl = getN('#produktRozmerVyska');
    if (sirkaEl) sirkaEl.value = produkt.rozmer.sirka;
    if (vyskaEl) vyskaEl.value = produkt.rozmer.vyska;
  }

  // Načtení materiálu
  if (getSelectedValue('#material', '0') === 'vlastni') {
    produkt.material.nazev = getSelectedText('#material', 'Matná křída');
    produkt.material.cena = +getValue('#materialCena', 0);
    produkt.material.gramaz = +getValue('#materialGramaz', 115);
    produkt.material.canFlip = true;
    // Poznámka: Rozměry materiálu se ve výpočtu nepoužívají - používají se rozměry strojů z nastavení
    produkt.material.rozmer.sirka = 500;  // Výchozí hodnota
    produkt.material.rozmer.vyska = 707;  // Výchozí hodnota
  } else {
    produkt.material.nazev = getSelectedText('#material', 'Matná křída');
    produkt.material.cena = +getSelectedValue('#material', '0');
    produkt.material.gramaz = +getSelectedAttribute('#material', 'data-gramaz', '115');
    // data-obratka = canFlip materiálu; '0' u jednostranně natíraných kartonů,
    // kde tisk na obrátku není možný (engine to řeší v safeCalcSheetsWithComp)
    produkt.material.canFlip = getSelectedAttribute('#material', 'data-obratka', '1') !== '0';
    // Opraveno: data-sirka pro sirka, data-vyska pro vyska (byl zde bug)
    produkt.material.rozmer.sirka = +getSelectedAttribute('#material', 'data-sirka', '500');
    produkt.material.rozmer.vyska = +getSelectedAttribute('#material', 'data-vyska', '707');
    const cenaEl = getN('#materialCena');
    const gramazEl = getN('#materialGramaz');
    if (cenaEl) cenaEl.value = produkt.material.cena;
    if (gramazEl) gramazEl.value = produkt.material.gramaz;
    // Odebrány: inputy pro rozměry materiálu již neexistují
  }

  // Načtení služeb
  produkt.laminace.typ = getSelectedValue('#laminace', '0');
  produkt.lak.typ = getSelectedValue('#lakovani', '0');
  produkt.falcovani.typ = getSelectedValue('#falcovani', '0');
  produkt.bigovani.typ = getSelectedValue('#bigovani', '0');
  
  produkt.vysek.option = getSelectedValue('#vysek', '0');
  produkt.vysek.pozicNaVysek = +getValue('#pozicNaVysek', 0);
  produkt.vysek.cenaRazniceVyseku = +getValue('#cenaRazniceVyseku', 0);
  
  produkt.predsek.option = +getSelectedValue('#predsek', '0');
  produkt.predsek.pozicNaPredsek = +getValue('#pozicNaPredsek', 0);
  produkt.predsek.cenaRaznicePredseku = +getValue('#cenaRaznicePredseku', 0);
  
  produkt.razba.option = +getSelectedValue('#razba', '0');
  produkt.razba.pozicNaRazbu = +getValue('#pozicNaRazbu', 0);
  produkt.razba.cenaStockuRazby = +getValue('#cenaStockuRazby', 0);
  
  produkt.vrtani.typ = getSelectedValue('#vrtani', '0');
  produkt.perforace.pocetPerforaci = +getSelectedValue('#perforace', '0');
  
  produkt.lepeni.typ = getSelectedValue('#lepeni', '0');
  produkt.lepeni.pocetLepeni = +getValue('#lepeniPocet', 0);

  // Načtení vazby
  produkt.vazba.typ = getSelectedValue('#vazba', '0');
  produkt.vazba.nazev = getSelectedText('#vazba', 'Bez vazby');
  produkt.vazba.orientace = getSelectedValue('#vazbaNa', 'vysku');
  produkt.vazba.orientaceText = getSelectedText('#vazbaNa', 'Na delší straně');
  produkt.vazba.pocetStran = +getValue('#vazbaPocetStran', 0);
  produkt.vazba.jinaObalka = getChecked('#vazbaJinaObalka', false);
  produkt.vazba.vnitrniTisk = getSelectedValue('#vazbaVnitrniTisk', 'jina');

  // Kroužková vazba: barva spirály + rozsah obálky (předek i záda / jen přední)
  const isKrouzekVazba = typeof produkt.vazba.typ === 'string' && produkt.vazba.typ.startsWith('krouzek');
  produkt.vazba.barvaSpiraly = isKrouzekVazba ? getSelectedValue('#vazbaBarvaSpiraly', 'bila') : null;
  produkt.vazba.obalkaZada = isKrouzekVazba && produkt.vazba.jinaObalka
    ? getSelectedValue('#vazbaObalkaZada', 'ano') === 'ano'
    : true;
  // Rozsah motivů u vazby: 'obalka' (vnitřek společný) | 'obalka-vnitrek'
  produkt.vazba.motivRozsah = getSelectedValue('#vazbaMotivRozsah', 'obalka');

  // Načtení vnitřního bloku (pokud je jiná obálka než vnitřní blok)
  if (produkt.vazba.typ !== '0' && produkt.vazba.jinaObalka) {
    const vnitrniMaterialVal = getSelectedValue('#vnitrniMaterial', '0');
    produkt.vazba.vnitrniBlok = {
      material: {
        nazev: getSelectedText('#vnitrniMaterial', ''),
        cena: vnitrniMaterialVal === 'vlastni' ? +getValue('#vnitrniMaterialCena', 0) : +vnitrniMaterialVal,
        gramaz: vnitrniMaterialVal === 'vlastni' ? +getValue('#vnitrniMaterialGramaz', 0) : +getSelectedAttribute('#vnitrniMaterial', 'data-gramaz', '0'),
        canFlip: vnitrniMaterialVal === 'vlastni' ? true : getSelectedAttribute('#vnitrniMaterial', 'data-obratka', '1') !== '0',
        rozmer: {
          sirka: +getSelectedAttribute('#vnitrniMaterial', 'data-sirka', '500'),
          vyska: +getSelectedAttribute('#vnitrniMaterial', 'data-vyska', '707'),
        },
      },
      barevnost: getSelectedValue('#vnitrniBarevnost', '4/4'),
      laminace: getSelectedValue('#vnitrniLaminace', '0'),
    };
  } else {
    produkt.vazba.vnitrniBlok = null;
  }

  // Načtení podložky (šedá lepenka) – pro lepené bloky a kroužkové vazby
  const isBlokVazba = produkt.vazba.typ === 'blok' || produkt.vazba.typ === 'blokPrebal';
  if ((isBlokVazba || isKrouzekVazba) && getChecked('#vazbaPodlozka', false)) {
    const podlozkaMaterialVal = getSelectedValue('#podlozkaMaterial', '0');
    produkt.vazba.podlozka = {
      enabled: true,
      material: {
        nazev: getSelectedText('#podlozkaMaterial', ''),
        cena: podlozkaMaterialVal === 'vlastni' ? +getValue('#podlozkaMaterialCena', 0) : +podlozkaMaterialVal,
        gramaz: podlozkaMaterialVal === 'vlastni' ? +getValue('#podlozkaMaterialGramaz', 0) : +getSelectedAttribute('#podlozkaMaterial', 'data-gramaz', '0'),
        canFlip: podlozkaMaterialVal === 'vlastni' ? true : getSelectedAttribute('#podlozkaMaterial', 'data-obratka', '1') !== '0',
        rozmer: {
          sirka: +getSelectedAttribute('#podlozkaMaterial', 'data-sirka', '500'),
          vyska: +getSelectedAttribute('#podlozkaMaterial', 'data-vyska', '707'),
        },
      },
    };
  } else {
    produkt.vazba.podlozka = null;
  }

  // Načtení objednávky
  objednavka.doprava.cena = +getSelectedValue('#doprava', '0');
  objednavka.doprava.text = getSelectedText('#doprava', 'Bez dopravy');
  if (objednavka.doprava.text == 'Bez dopravy') {
    objednavka.doprava.text = "";
  } else {
    objednavka.doprava.text = 'Doprava: ' + objednavka.doprava.text + '<br>';
  }

  return { produkt, nastaveni, zakaznik, objednavka };
}

// Funkce pro komunikaci s backend API
async function calculatePriceAPI(data) {
  try {
    const response = await fetch(`${API_BASE_URL}/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Chyba při komunikaci s backend API:', error);
    throw error;
  }
}

// Hlavní funkce pro výpočet
async function pocitej() {
  validateDokoncovaciLimity();
  // Dokud není auth ověřený (gate), nevolej kalkulační API – nepřihlášený
  // klient by dostával 401. Po odemčení aplikace se výpočet spustí znovu.
  if (!window.__appReady) return;
  try {
    // Sezbíráme form data
    const formData = gatherForm();
    
    // Označit výsledky jako "počítám" – obsah NEmažeme (zabrání skoku velikosti),
    // jen ho ztlumíme. Pokud je kostka ještě prázdná (první výpočet), ukážeme spinner.
    setResultUpdating(document.getElementById('mail'), true);
    setResultUpdating(document.getElementById('technicke'), true);
    
    // Pošleme data na backend
    const result = await calculatePriceAPI(formData);
    
    if (result.success) {
      // Validace dat z API - ochrana proti null/undefined hodnotám (např. Infinity v JSON)
      const r = result.result;
      if (r.cenaCelkem == null || r.cenaSDphZaKus == null || !isFinite(r.cenaCelkem)) {
        throw new Error('Kalkulace vrátila neplatné hodnoty. Zkontrolujte rozměry produktu a zvolenou technologii tisku.');
      }

      // Zobrazíme výsledky s ikonkou copy
      const copyButton = `
        <button type="button" onclick="copyCalculationResult()" class="absolute bottom-2 right-2 p-1 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded transition-colors">
          ${icon('document-duplicate', 'w-4 h-4')}
        </button>
      `;
      swapResultContent(document.getElementById('mail'), r.textOutput + copyButton);
      swapResultContent(document.getElementById('technicke'), '<p><strong>Info: </strong>' + pripravRozkresHtml(r.debugInfo) + '</p>');
      setResultUpdating(document.getElementById('mail'), false);
      setResultUpdating(document.getElementById('technicke'), false);
      
      // Mobil výsledek
      const mobilResult = r.cenaCelkem.toLocaleString('cs-CZ') + ' Kč (<strong>' +
        r.cenaSDphZaKus.toFixed(2) + ' Kč/ks</strong>)';
      const mobilCenaEl = document.getElementById('vysledekMobilCena');
      if (mobilCenaEl) mobilCenaEl.innerHTML = mobilResult;
      
      // Aktualizovat cenu v položkách nabídky
      if (window.offerItemsManager) {
        window.offerItemsManager.updateItemPrice(
          r.cenaCelkem,
          r.cenaSDphZaKus,
          r.debugInfo,
          r
        );
      }
      
    } else {
      throw new Error(result.error || 'Neznámá chyba');
    }
  } catch (error) {
    console.error('Chyba při výpočtu:', error);
    // Hezké zobrazení chyby s ikonkou varování
    const errorHtml = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          ${icon('exclamation-triangle', 'w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0')}
          <div>
            <h3 class="text-sm font-medium text-red-800">Chyba při výpočtu</h3>
            <p class="text-sm text-red-700 mt-1">${error.message}</p>
          </div>
        </div>
      </div>
    `;
    swapResultContent(document.getElementById('mail'), errorHtml);
    swapResultContent(document.getElementById('technicke'), '');
    setResultUpdating(document.getElementById('mail'), false);
    setResultUpdating(document.getElementById('technicke'), false);
    const mobilCenaErr = document.getElementById('vysledekMobilCena');
    if (mobilCenaErr) mobilCenaErr.innerHTML = '<span class="text-red-500">Chyba</span>';
  }
}

// -- Plynulý překreslení výsledku kalkulace ------------------------------
// Místo toho, abychom při každém přepočtu přepsali #mail/#technicke na
// spinner (obsah zmizí, kostka se zmenší a po odpovědi zas zvětší -> UI skáče),
// necháme starý obsah na místě, jen ho ztlumíme (.is-updating). Nový obsah
// pak vložíme atomicky a pokud se změnila výška, plynule ji animujeme.

// Označí kostku výsledku jako "počítám" (ztlumí obsah). Při prvním výpočtu,
// když je kostka prázdná, vložíme naivní spinner, ať uživatel něco vidí.
function setResultUpdating(el, updating) {
  if (!el) return;
  if (updating && !el.innerHTML.trim()) {
    el.innerHTML = '<div class="calc-spinner" role="status" aria-label="Načítám"></div>';
  }
  el.classList.toggle('is-updating', updating);
}

// Atomická výměna obsahu s krátkou animací výšky (height: auto -> px -> auto).
// Pokud se výška nemění, pouze vyměníme obsah bez animace.
function swapResultContent(el, newHtml) {
  if (!el) return;
  // Zrušit případný probíhající přechod z předchozí výměny.
  if (el._swapCleanup) { el._swapCleanup(); el._swapCleanup = null; }

  const startHeight = el.offsetHeight;
  // První vyplnění (dosud prázdná / nulová výška) – bez animace.
  if (!el.innerHTML.trim() || startHeight === 0) {
    el.innerHTML = newHtml;
    return;
  }
  // Zafixovat startovní výšku, vyměnit obsah a změřit cílovou výšku.
  el.style.overflow = 'hidden';
  el.style.height = startHeight + 'px';
  el.innerHTML = newHtml;
  const endHeight = el.scrollHeight;
  if (Math.abs(endHeight - startHeight) < 2) {
    el.style.height = '';
    el.style.overflow = '';
    return;
  }
  // Force reflow na startovní výšce, pak přejdeme na cílovou -> animace.
  void el.offsetHeight;
  el.style.height = endHeight + 'px';

  const finish = () => {
    el.style.height = '';
    el.style.overflow = '';
    el.removeEventListener('transitionend', onTransitionEnd);
    el._swapCleanup = null;
    if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
  };
  const onTransitionEnd = (ev) => { if (ev && ev.propertyName !== 'height') return; finish(); };
  el.addEventListener('transitionend', onTransitionEnd);
  // Fallback pro případ, že transitionend nevystřelí (např. zrušená animace).
  let fallbackTimer = setTimeout(finish, 450);
  el._swapCleanup = () => {
    el.removeEventListener('transitionend', onTransitionEnd);
    if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
  };
}

// Event listenery pro formulář
// Flag: během aplikace rychlé předvolby (applyPreset) tyto "reset na vlastní"
// listenery vypneme, aby nepřepsaly Formát/Materiál nastavený presetem.
window._applyingPreset = false;

document.querySelectorAll('#materialCena, #materialGramaz').forEach(function (item) {
  const resetMaterial = function () {
    if (window._applyingPreset) return;
    document.querySelector('#material').value = 'vlastni';
  };
  item.addEventListener('keyup', resetMaterial);
  item.addEventListener('change', resetMaterial);
});

document.querySelectorAll('#produktRozmerSirka, #produktRozmerVyska').forEach(function (item) {
  const resetFormat = function () {
    if (window._applyingPreset) return;
    document.querySelector('#produktRozmerFormat').value = 'vlastni';
  };
  item.addEventListener('keyup', resetFormat);
  item.addEventListener('change', resetFormat);
});

// Inicializace dropdownu pro rychlý výběr počtu kusů
function setupPocetKusuDropdown() {
  const dropdown = document.getElementById('pocetKusuDropdown');
  const dropdownBtn = document.getElementById('pocetKusuDropdownBtn');
  const input = document.getElementById('pocetKusu');
  const presetButtons = document.querySelectorAll('.pocet-preset');

  if (!dropdown || !dropdownBtn || !input) return;

  // Toggle dropdown při kliknutí na tlačítko
  dropdownBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('hidden');
  });

  // Nastavení hodnoty při kliknutí na preset
  presetButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const value = btn.getAttribute('data-value');
      input.value = value;
      dropdown.classList.add('hidden');

      // Trigger změnu pro přepočítání
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  // Zavření dropdownu při kliknutí mimo
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target) && e.target !== dropdownBtn) {
      dropdown.classList.add('hidden');
    }
  });

  // Zavření dropdownu při ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !dropdown.classList.contains('hidden')) {
      dropdown.classList.add('hidden');
    }
  });
}

// Inicializace dropdownu pro rychlý výběr počtu stran vazby
function setupPocetStranDropdown() {
  const dropdown = document.getElementById('pocetStranDropdown');
  const dropdownBtn = document.getElementById('pocetStranDropdownBtn');
  const input = document.getElementById('vazbaPocetStran');
  const presetButtons = document.querySelectorAll('.stran-preset');

  if (!dropdown || !dropdownBtn || !input) return;

  // Toggle dropdown při kliknutí na tlačítko
  dropdownBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('hidden');
  });

  // Nastavení hodnoty při kliknutí na preset
  presetButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const value = btn.getAttribute('data-value');
      input.value = value;
      dropdown.classList.add('hidden');

      // Trigger změnu pro přepočítání a validaci
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  // Zavření dropdownu při kliknutí mimo
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target) && e.target !== dropdownBtn) {
      dropdown.classList.add('hidden');
    }
  });

  // Zavření dropdownu při ESC
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !dropdown.classList.contains('hidden')) {
      dropdown.classList.add('hidden');
    }
  });
}

// Inicializace event listenerů po načtení DOM
document.addEventListener('DOMContentLoaded', () => {
  // Změna ve formuláři, počítej znovu
  const vypocetForm = document.querySelector('#vypocet');
  const cenyForm = document.querySelector('#ceny');
  
  // Debounce – při psaní (keyup) přepočítáme až po 250 ms klidu,
  // aby jsme nezahlcovali backend při rychlém ťukání do číselných polí.
  // Při change (select / blur) přepočítáme okamžitě – uživatel dokončil akci.
  let pocitejTimer = null;
  function debouncedPocitej(delay = 250) {
    if (pocitejTimer) clearTimeout(pocitejTimer);
    pocitejTimer = setTimeout(() => { pocitejTimer = null; pocitej(); }, delay);
  }

  if (vypocetForm) {
    vypocetForm.addEventListener('keyup', function () { if (!window._applyingPreset) debouncedPocitej(); });
    vypocetForm.addEventListener('change', function () { if (!window._applyingPreset) pocitej(); });
  }

  if (cenyForm) {
    cenyForm.addEventListener('keyup', function () { if (!window._applyingPreset) debouncedPocitej(); });
    cenyForm.addEventListener('change', function () { if (!window._applyingPreset) pocitej(); });
  }

  // Pokud je název položky mimo formulář #vypocet, přidej přepočet i na něj
  const nazevInput = document.getElementById('nazev');
  if (nazevInput && !nazevInput.closest('#vypocet')) {
    nazevInput.addEventListener('keyup', function () { debouncedPocitej(); });
    nazevInput.addEventListener('change', function () { pocitej(); });
  }

  // Quick presets – kliknutím na chip nastavíme klíčová pole a přepočítáme
  setupQuickPresets();

  // Progressive disclosure – sbalení sekce "Dokončování"
  setupCollapsibleSections();

  // Inicializace lokálního nastavení technologie tisku
  initTechnologieListener();

  // Počáteční výpočet po načtení stránky
  setTimeout(() => pocitej(), 500);
  
  // Event listenery pro profil formulář
  setupProfileFormListeners();
  
  // Inicializace podmíněných inputů (skrývání/zobrazování)
  setupConditionalInputs();

  // Inicializace dropdownu pro počet kusů
  setupPocetKusuDropdown();

  // Inicializace dropdownu pro počet stran vazby
  setupPocetStranDropdown();
});

// Profile form event listeners
function setupProfileFormListeners() {
  const profileForm = document.getElementById('profile-form');
  const profileCancelBtn = document.getElementById('profile-cancel-btn');
  
  if (profileForm) {
    profileForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const formData = new FormData(profileForm);
      const profileData = {
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone')
      };
      
      try {
        // Uložit profil (jméno, email, telefon)
        const response = await fetch(`${API_BASE_URL}/user/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(profileData)
        });

        const data = await response.json();
        
        if (response.ok) {
          // Aktualizuj data v localStorage
          const currentUser = JSON.parse(localStorage.getItem('currentUser'));
          currentUser.name = data.data.name;
          currentUser.email = data.data.email;
          currentUser.phone = data.data.phone;
          localStorage.setItem('currentUser', JSON.stringify(currentUser));
          
          // Aktualizuj currentUser v authManager
          if (authManager) {
            authManager.currentUser = currentUser;
            authManager.updateUI();
          }
          
          // Uložit preference (showHourlyCalc) - oddělený API call
          const showHourlyCalcCheckbox = document.getElementById('profile-show-hourly-calc');
          const showHourlyCalc = showHourlyCalcCheckbox ? showHourlyCalcCheckbox.checked : false;
          
          const prefsResponse = await fetch(`${API_BASE_URL}/user/preferences`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ showHourlyCalc })
          });
          
          if (prefsResponse.ok) {
            showNotification('Profil a preference byly úspěšně uloženy', 'success');
          } else {
            showNotification(data.message || 'Profil uložen, ale chyba při ukládání preferencí', 'warning');
          }
        } else {
          showNotification(data.message || 'Chyba při ukládání profilu', 'error');
        }
      } catch (error) {
        console.error('Chyba při ukládání profilu:', error);
        showNotification('Chyba spojení se serverem', 'error');
      }
    });
  }
  
  if (profileCancelBtn) {
    profileCancelBtn.addEventListener('click', () => {
      // Znovu načti původní data
      if (authManager && authManager.isLoggedIn()) {
        authManager.loadProfileData();
      }
    });
  }
  
  // Inicializuj listener pro změnu hesla
  setupPasswordChangeListener();
}

// Nová funkce pro změnu hesla s validací silného hesla
function setupPasswordChangeListener() {
  const changePasswordBtn = document.getElementById('change-password-btn');
  
  if (changePasswordBtn) {
    changePasswordBtn.addEventListener('click', async () => {
      const oldPassword = document.getElementById('profile-old-password').value;
      const newPassword = document.getElementById('profile-new-password').value;
      const confirmPassword = document.getElementById('profile-confirm-password').value;
      
      // Validace na frontendu
      if (!oldPassword || !newPassword || !confirmPassword) {
        showNotification('Vyplňte všechna pole', 'warning');
        return;
      }
      
      // Validace minimální délky
      if (newPassword.length < 8) {
        showNotification('Nové heslo musí mít alespoň 8 znaků', 'warning');
        return;
      }
      
      // Validace malého písmena
      if (!/[a-z]/.test(newPassword)) {
        showNotification('Heslo musí obsahovat alespoň jedno malé písmeno', 'warning');
        return;
      }
      
      // Validace velkého písmena
      if (!/[A-Z]/.test(newPassword)) {
        showNotification('Heslo musí obsahovat alespoň jedno velké písmeno', 'warning');
        return;
      }
      
      // Validace čísla
      if (!/[0-9]/.test(newPassword)) {
        showNotification('Heslo musí obsahovat alespoň jedno číslo', 'warning');
        return;
      }
      
      // Validace speciálního znaku
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
        showNotification('Heslo musí obsahovat alespoň jeden speciální znak (!@#$%^&* atd.)', 'warning');
        return;
      }
      
      // Validace shody hesel
      if (newPassword !== confirmPassword) {
        showNotification('Nová hesla se neshodují', 'warning');
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/user/password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ oldPassword, newPassword })
        });

        const data = await response.json();
        
        if (response.ok) {
          showNotification('Heslo bylo úspěšně změněno', 'success');
          
          // Vyčisti formulář
          document.getElementById('profile-old-password').value = '';
          document.getElementById('profile-new-password').value = '';
          document.getElementById('profile-confirm-password').value = '';
        } else {
          showNotification(data.message || 'Chyba při změně hesla', 'error');
        }
      } catch (error) {
        console.error('Chyba při změně hesla:', error);
        showNotification('Chyba spojení se serverem', 'error');
      }
    });
  }
}

// Správa sekcí hlavního menu
class SectionManager {
  constructor() {
    this.currentSection = 'dashboard';
    this.sections = {
      'dashboard': document.getElementById('dashboard-section'),
      'kalkulator': document.getElementById('kalkulator-section'),
      'nabidky': document.getElementById('nabidky-section'),
      'nabidka-editor': document.getElementById('nabidka-editor-section'),
      'objednavky': document.getElementById('objednavky-section'),
      'vyroba': document.getElementById('vyroba-section'),
      'faktury': document.getElementById('faktury-section'),
      'zakaznici': document.getElementById('zakaznici-section'),
      'zakaznik-detail': document.getElementById('zakaznik-detail-section'),
      'soubory': document.getElementById('soubory-section'),
      'papiry': document.getElementById('papiry-section'),
      'stroje': document.getElementById('stroje-section'),
      'nastaveni': document.getElementById('nastaveni-section'),
      'uzivatele': document.getElementById('uzivatele-section'),
      'ai-asistent': document.getElementById('ai-asistent-section'),
      'profil': document.getElementById('profil-section'),
      'public-offer': document.getElementById('public-offer-section'),
      'public-files': document.getElementById('public-files-section')
    };
    this.menuItems = {
      'dashboard': document.querySelector('[data-section="dashboard"]'),
      'kalkulator': document.querySelector('[data-section="kalkulator"]'),
      'nabidky': document.querySelector('[data-section="nabidky"]'),
      'objednavky': document.querySelector('[data-section="objednavky"]'),
      'vyroba': document.querySelector('[data-section="vyroba"]'),
      'faktury': document.querySelector('[data-section="faktury"]'),
      'zakaznici': document.querySelector('[data-section="zakaznici"]'),
      'soubory': document.querySelector('[data-section="soubory"]'),
      'papiry': document.querySelector('[data-section="papiry"]'),
      'stroje': document.querySelector('[data-section="stroje"]'),
      'nastaveni': document.querySelector('[data-section="nastaveni"]'),
      'uzivatele': document.querySelector('[data-section="uzivatele"]'),
      'ai-asistent': document.querySelector('[data-section="ai-asistent"]')
    };
    
    // Debug: Zkontroluj jestli jsou všechny sekce a menu položky nalezeny
    console.log('SectionManager - sections:', this.sections);
    console.log('SectionManager - menuItems:', this.menuItems);
    
    this.init();
  }
  
  init() {
    // Pokud je to veřejná nabídka, zobraz ji a neinicializuj menu
    if (window.isPublicOfferView) {
      this.switchToSection('public-offer');
      // Inicializovat PublicOfferManager a načíst nabídku
      if (!window.publicOfferManager) {
        window.publicOfferManager = new PublicOfferManager();
      }
      if (window.publicOfferToken) {
        window.publicOfferManager.loadOfferByToken(window.publicOfferToken);
      }
      return;
    }

    // Pokud je to veřejná stránka souborů, zobraz ji a neinicializuj menu
    if (window.isPublicFilesView) {
      this.switchToSection('public-files');
      if (!window.publicFilesManager) {
        window.publicFilesManager = new PublicFilesManager();
      }
      if (window.publicFilesToken) {
        window.publicFilesManager.loadFilesByToken(window.publicFilesToken);
      }
      return;
    }

    // Přidej event listenery na VŠECHNY menu položky (desktop i mobile)
    Object.keys(this.sections).forEach(sectionId => {
      // Přeskoč veřejné sekce (nemají menu)
      if (sectionId === 'public-offer' || sectionId === 'public-files') return;
      
      // Najdi VŠECHNY elementy s daným data-section atributem
      const menuItems = document.querySelectorAll(`[data-section="${sectionId}"]`);
      
      // Přidej event listener na každý nalezený element
      menuItems.forEach(menuItem => {
        if (menuItem) {
          menuItem.addEventListener('click', (e) => {
            e.preventDefault();
            this.switchToSection(sectionId);
          });
        }
      });
    });
    
    // Zobraz počáteční sekci
    this.switchToSection('kalkulator');
  }
  
  switchToSection(sectionId, options = {}) {
    const skipLoad = Boolean(options.skipLoad);
    console.log(`SectionManager.switchToSection called with: ${sectionId}`);
    
    // Pro veřejné read-only stránky přeskoč kontrolu přihlášení a menu styling
    if (sectionId === 'public-offer' || sectionId === 'public-files') {
      // Skryj všechny sekce pomocí stejné logiky jako pro normální sekce
      Object.values(this.sections).forEach(section => {
        if (section) {
          section.classList.remove('active', 'hidden');
          section.style.display = '';
        }
      });

      // Zobraz cílovou veřejnou sekci přidáním 'active' třídy
      if (this.sections[sectionId]) {
        this.sections[sectionId].classList.add('active');
      }

      this.currentSection = sectionId;
      window.autoRefreshManager?.setCurrentSection(sectionId);
      return;
    }
    
    // Kontrola přihlášení pro chráněné sekce
    const protectedSections = ['dashboard', 'nastaveni', 'uzivatele', 'nabidky', 'nabidka-editor', 'objednavky', 'vyroba', 'faktury', 'zakaznici', 'zakaznik-detail', 'soubory', 'papiry', 'profil', 'ai-asistent'];
    if (protectedSections.includes(sectionId) && authManager && !authManager.isLoggedIn()) {
      const sectionNames = {
        'dashboard': 'dashboardu',
        'nastaveni': 'nastavení',
        'uzivatele': 'správě uživatelů',
        'nabidky': 'nabídkám',
        'nabidka-editor': 'editoru nabídky',
        'objednavky': 'objednávkám',
        'vyroba': 'technologickým listům',
        'faktury': 'fakturám',
        'zakaznici': 'zákazníkům',
        'zakaznik-detail': 'detailu zákazníka',
        'soubory': 'souborům',
        'profil': 'nastavení profilu',
        'papiry': 'papírům',
        'ai-asistent': 'AI asistentovi'
      };
      console.log(`User not logged in for protected section: ${sectionId}`);
      // Druhá vrstva obrany – nepřihlášený uživatel vidí jen auth gate,
      // sem by se neměl dostat. Jen upozorni a nepřepínej sekci.
      showNotification(`Pro přístup k ${sectionNames[sectionId]} se musíte přihlásit`, 'warning');
      return;
    }
    
    // Skryj mobilní drawer po kliknutí na menu položku
    if (window.mobileMenu && window.mobileMenu.isVisible()) {
      window.mobileMenu.hide();
    }

    // Skryj všechny sekce pomocí custom CSS tříd
    Object.values(this.sections).forEach(section => {
      if (section) {
        section.classList.remove('active', 'hidden');
        // Odstraň inline styles pokud existují
        section.style.display = '';
      }
    });
    
    // Odstraň aktivní stav ze všech menu položek (Tailwind třídy) - desktop i mobile
    Object.keys(this.sections).forEach(key => {
      const allMenuItems = document.querySelectorAll(`[data-section="${key}"]`);
      allMenuItems.forEach(menuItem => {
        if (menuItem) {
          // Odstraň aktivní Tailwind třídy
          menuItem.classList.remove('text-white', 'bg-blue-600', 'shadow-sm');
          // Přidej neaktivní Tailwind třídy
          menuItem.classList.add('text-gray-600', 'hover:text-blue-600', 'hover:bg-blue-50');
        }
      });
    });
    
    // Resetuj aktivní stav user dropdown tlačítka
    const userDropdownBtn = document.getElementById('user-dropdown-btn');
    if (userDropdownBtn) {
      userDropdownBtn.classList.remove('text-white', 'bg-blue-600', 'shadow-sm');
      userDropdownBtn.classList.add('text-gray-600', 'hover:text-blue-600', 'hover:bg-blue-50');
    }
    
    // Zobraz aktivní sekci pomocí custom CSS tříd
    const activeSection = this.sections[sectionId];
    console.log(`Active section for ${sectionId}:`, activeSection);
    if (activeSection) {
      activeSection.classList.add('active');
      console.log(`Section ${sectionId} shown - classes:`, activeSection.className);
    } else {
      console.error(`Section ${sectionId} not found!`);
    }
    
    // Označ aktivní menu položku (Tailwind třídy) - desktop i mobile
    const activeMenuItems = document.querySelectorAll(`[data-section="${sectionId}"]`);
    activeMenuItems.forEach(activeMenuItem => {
      if (activeMenuItem) {
        // Odstraň neaktivní Tailwind třídy
        activeMenuItem.classList.remove('text-gray-600', 'hover:text-blue-600', 'hover:bg-blue-50');
        // Přidej aktivní Tailwind třídy
        activeMenuItem.classList.add('text-white', 'bg-blue-600', 'shadow-sm');
      }
    });
    
    // Pokud je aktivní sekce profil, označ user dropdown jako aktivní
    if (sectionId === 'profil' && userDropdownBtn) {
      userDropdownBtn.classList.remove('text-gray-600', 'hover:text-blue-600', 'hover:bg-blue-50');
      userDropdownBtn.classList.add('text-white', 'bg-blue-600', 'shadow-sm');
    }
    
    this.currentSection = sectionId;
    window.autoRefreshManager?.setCurrentSection(sectionId);
    
    // Pokud je aktivní kalkulátor, znovu spustí výpočet
    if (sectionId === 'kalkulator') {
      setTimeout(() => pocitej(), 100);
    }
    
    if (!skipLoad) {
      // Pokud je aktivní sekce nabídek, obnovit seznam
      if (sectionId === 'nabidky' && window.offersListManager) {
        window.offersListManager.refresh();
      }

      // Pokud je aktivní sekce zákazníků, načíst a vykreslit
      if (sectionId === 'zakaznici' && window.customersManager) {
        window.customersManager.showSection();
      }

      // Pokud je aktivní sekce objednávek, načíst a vykreslit
      if (sectionId === 'objednavky' && window.ordersListManager) {
        window.ordersListManager.showSection();
      }

      // Pokud je aktivní sekce výroby (technologické listy), načíst a vykreslit
      if (sectionId === 'vyroba' && window.vyrobaManager) {
        window.vyrobaManager.showSection();
      }

      // Pokud je aktivní sekce faktur, načíst a vykreslit
      if (sectionId === 'faktury' && window.invoicesListManager) {
        window.invoicesListManager.showSection();
      }

      // Pokud je aktivní sekce dashboardu, načíst a vykreslit KPI
      if (sectionId === 'dashboard' && window.dashboardManager) {
        window.dashboardManager.showSection();
      }

      // Pokud je aktivní sekce souborů, načíst a vykreslit
      if (sectionId === 'soubory' && window.filesManager) {
        window.filesManager.showSection();
      }

      // Pokud je aktivní sekce nastavení a uživatel je admin, načti API klíče
      // a OAuth klienty (obě sekce + nav skupina jsou data-admin-only).
      if (sectionId === 'nastaveni') {
        const role = window.authManager?.currentUser?.role;
        const isAdmin = role === 'admin';
        document.querySelectorAll('[data-admin-only]').forEach((el) => {
          el.classList.toggle('hidden', !isAdmin);
        });
        if (isAdmin) {
          if (window.apiKeysAdmin) window.apiKeysAdmin.load();
          if (window.oauthClientsAdmin) window.oauthClientsAdmin.load();
        }
      }

      // Pokud je aktivní sekce uživatelů, načíst a vykreslit
      if (sectionId === 'uzivatele' && window.usersAdmin) {
        window.usersAdmin.showSection();
      }
    }
  }
  
  // Alias pro switchToSection pro kompatibilitu
  showSection(sectionId, options) {
    this.switchToSection(sectionId, options);
  }
}

// Inicializace správce sekcí (pro všechny stránky včetně veřejné nabídky)
const sectionManager = new SectionManager();
window.sectionManager = sectionManager;

function initSettingsNav() {
  const nav = document.getElementById('settings-nav');
  if (!nav) return;

  const links = Array.from(nav.querySelectorAll('.settings-nav__link'));
  if (!links.length) return;

  const targets = links
    .map(link => {
      const id = link.getAttribute('href')?.replace('#', '');
      const el = id ? document.getElementById(id) : null;
      return el ? { link, el } : null;
    })
    .filter(Boolean);

  if (!targets.length) return;

  const setActive = (activeLink) => {
    links.forEach(l => l.classList.remove('is-active'));
    if (activeLink) activeLink.classList.add('is-active');
  };

  // Výchozí aktivní = první dostupný odkaz (typicky "Obecné")
  setActive(targets[0].link);

  links.forEach(link => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href')?.replace('#', '');
      const el = id ? document.getElementById(id) : null;
      if (!el) return;
      e.preventDefault();
      setActive(link);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `#${id}`);
    });
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      // Vyber nejvrchnější viditelný cíl
      const visible = entries
        .filter(en => en.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (!visible.length) return;
      const topId = visible[0].target.id;
      const match = targets.find(t => t.el.id === topId);
      if (match) setActive(match.link);
    }, {
      rootMargin: '-96px 0px -60% 0px',
      threshold: 0
    });
    targets.forEach(t => observer.observe(t.el));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSettingsNav);
} else {
  initSettingsNav();
}

// Inicializace auth manageru (s error handling) - pouze pokud to není veřejná nabídka
let authManager;
if (!window.isPublicOfferView) {
  try {
    authManager = new AuthManager();
  } catch (error) {
    console.error('Chyba při inicializaci AuthManager:', error);
    // Vytvoř dummy objekt aby nedošlo k dalším chybám
    authManager = {
      isLoggedIn: () => false,
      updateUI: () => {},
      getAuthHeaders: () => ({}),
      showSessionExpiredModal: () => {},
      hideSessionExpiredModal: () => {}
    };
  }
} else {
  // Pro veřejnou nabídku vytvoř dummy authManager
  authManager = {
    isLoggedIn: () => false,
    updateUI: () => {},
    getAuthHeaders: () => ({}),
    showSessionExpiredModal: () => {},
    hideSessionExpiredModal: () => {}
  };
}
window.authManager = authManager;

// Inicializace správce položek nabídky (pouze pokud to není veřejná nabídka)
let offerItemsManager;
if (!window.isPublicOfferView) {
  try {
    offerItemsManager = new OfferItemsManager();
    window.offerItemsManager = offerItemsManager;
  } catch (error) {
    console.error('Chyba při inicializaci OfferItemsManager:', error);
  }
}

// Inicializace správce seznamu nabídek (pouze pokud to není veřejná nabídka)
let offersListManager;
if (!window.isPublicOfferView) {
  try {
    offersListManager = new OffersListManager();
    window.offersListManager = offersListManager;
  } catch (error) {
    console.error('Chyba při inicializaci OffersListManager:', error);
  }
}

// Inicializace správce editoru nabídky (pouze pokud to není veřejná nabídka)
let offerEditorManager;
if (!window.isPublicOfferView) {
  try {
    offerEditorManager = new OfferEditorManager();
    window.offerEditorManager = offerEditorManager;
  } catch (error) {
    console.error('Chyba při inicializaci OfferEditorManager:', error);
  }
}

// Inicializace správce zákazníků (pouze pokud to není veřejná nabídka)
let customersManager;
if (!window.isPublicOfferView) {
  try {
    customersManager = new CustomersManager();
    window.customersManager = customersManager;
  } catch (error) {
    console.error('Chyba při inicializaci CustomersManager:', error);
  }
}

// Inicializace správce objednávek (pouze pokud to není veřejná nabídka)
let ordersListManager;
if (!window.isPublicOfferView) {
  try {
    ordersListManager = new OrdersListManager();
    window.ordersListManager = ordersListManager;
  } catch (error) {
    console.error('Chyba při inicializaci OrdersListManager:', error);
  }
}

// Inicializace správce výroby (pouze pokud to není veřejná nabídka)
let vyrobaManager;
if (!window.isPublicOfferView) {
  try {
    vyrobaManager = new VyrobaManager();
    window.vyrobaManager = vyrobaManager;
  } catch (error) {
    console.error('Chyba při inicializaci VyrobaManager:', error);
  }
}

// Inicializace správce dashboardu (pouze pokud to není veřejná nabídka)
let dashboardManager;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    dashboardManager = new DashboardManager();
    window.dashboardManager = dashboardManager;
  } catch (error) {
    console.error('Chyba při inicializaci DashboardManager:', error);
  }
}

// Inicializace správce faktur (pouze pokud to není veřejná nabídka)
let invoicesListManager;
if (!window.isPublicOfferView) {
  try {
    invoicesListManager = new InvoicesListManager();
    window.invoicesListManager = invoicesListManager;
    // Pro snadný přístup ze customers.js (status meta)
    window.InvoicesListManager = InvoicesListManager;
  } catch (error) {
    console.error('Chyba při inicializaci InvoicesListManager:', error);
  }
}

// Inicializace správce souborů (pouze pokud to není veřejný read-only pohled)
let filesManager;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    filesManager = new FilesManager();
    window.filesManager = filesManager;
  } catch (error) {
    console.error('Chyba při inicializaci FilesManager:', error);
  }
}

// Inicializace správce API klíčů (admin-only, aktivuje se po prvním otevření
// sekce Nastavení – aby se zbytečně nestahoval seznam před přihlášením).
let apiKeysAdmin;
let oauthClientsAdmin;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    apiKeysAdmin = new ApiKeysAdmin();
    window.apiKeysAdmin = apiKeysAdmin;
  } catch (error) {
    console.error('Chyba při inicializaci ApiKeysAdmin:', error);
  }
  try {
    oauthClientsAdmin = new OAuthClientsAdmin();
    window.oauthClientsAdmin = oauthClientsAdmin;
  } catch (error) {
    console.error('Chyba při inicializaci OAuthClientsAdmin:', error);
  }
}

// Inicializace správce uživatelů (admin only, pouze pokud to není veřejný read-only pohled)
let usersAdmin;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    usersAdmin = new UsersAdmin();
    window.usersAdmin = usersAdmin;
  } catch (error) {
    console.error('Chyba při inicializaci UsersAdmin:', error);
  }
}

// Automatická obnova aktivní sekce (poll + návrat do tabu/focus).
let autoRefreshManager;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    autoRefreshManager = initAutoRefresh({ intervalMs: 600_000 });
    window.autoRefreshManager = autoRefreshManager;
    autoRefreshManager.setCurrentSection(sectionManager.currentSection);
  } catch (error) {
    console.error('Chyba při inicializaci AutoRefreshManager:', error);
  }
}

// True push: SSE notifikace změn ze serveru → okamžitý refresh aktivní sekce.
let realtimePushManager;
if (!window.isPublicOfferView && !window.isPublicFilesView) {
  try {
    realtimePushManager = initRealtimePush({
      onDataChange: (eventPayload) => {
        window.autoRefreshManager?.handlePushEvent(eventPayload);
      },
    });
    window.realtimePushManager = realtimePushManager;
  } catch (error) {
    console.error('Chyba při inicializaci RealtimePushManager:', error);
  }
}

// ============================================================================
// MOTIVY – vlastní počty kusů pro jednotlivé druhy/motivy
// Panel se zobrazí při Počet druhů >= 2. Počet kusů se přepne na dopočítanou
// sumu (jeden zdroj pravdy = řádky motivů). Názvy motivů putují do nabídky,
// objednávky i technických listů.
// ============================================================================

function getMotivyRows() {
  const rows = [];
  document.querySelectorAll('#motivy-rows [data-motiv-row]').forEach((row) => {
    const nazev = row.querySelector('[data-motiv-nazev]')?.value?.trim() || '';
    const pocetKusu = Math.max(0, Math.round(+(row.querySelector('[data-motiv-kusu]')?.value || 0)));
    rows.push({ nazev, pocetKusu });
  });
  return rows;
}

function updateMotivySum() {
  const rows = getMotivyRows();
  const sum = rows.reduce((a, m) => a + m.pocetKusu, 0);
  const sumEl = document.getElementById('motivy-celkem');
  if (sumEl) sumEl.textContent = sum;
  const pocetKusuInput = document.getElementById('pocetKusu');
  if (pocetKusuInput && rows.length > 1) pocetKusuInput.value = sum;
  return sum;
}

function renderMotivyPanel(presetMotivy = null) {
  const panel = document.getElementById('motivy-panel');
  const rowsBox = document.getElementById('motivy-rows');
  const pocetDruhuInput = document.getElementById('pocetDruhu');
  const pocetKusuInput = document.getElementById('pocetKusu');
  const pocetKusuDropdownBtn = document.getElementById('pocetKusuDropdownBtn');
  if (!panel || !rowsBox || !pocetDruhuInput || !pocetKusuInput) return;

  const n = Math.max(1, Math.round(+pocetDruhuInput.value || 1));

  if (n < 2) {
    // Zpět na jeden druh: Počet kusů = dosavadní celkový náklad.
    if (!panel.classList.contains('hidden')) {
      const sum = updateMotivySum();
      if (sum > 0) pocetKusuInput.value = sum;
    }
    panel.classList.add('hidden');
    rowsBox.innerHTML = '';
    pocetKusuInput.disabled = false;
    pocetKusuDropdownBtn?.classList.remove('hidden');
    return;
  }

  // Zachovat stávající hodnoty řádků; nové řádky předvyplnit.
  const existing = getMotivyRows();
  const fallbackKusu = existing.length
    ? (existing[existing.length - 1].pocetKusu || 0)
    : Math.max(0, Math.round(+pocetKusuInput.value || 0));

  const values = [];
  for (let i = 0; i < n; i++) {
    if (presetMotivy && presetMotivy[i]) {
      values.push({ nazev: String(presetMotivy[i].nazev || ''), pocetKusu: +presetMotivy[i].pocetKusu || 0 });
    } else if (existing[i]) {
      values.push(existing[i]);
    } else {
      values.push({ nazev: '', pocetKusu: fallbackKusu });
    }
  }

  rowsBox.innerHTML = values.map((m, i) => `
    <div data-motiv-row class="grid grid-cols-[24px_minmax(0,1fr)_110px_28px] gap-2 items-center mb-2">
      <span class="text-xs text-blue-700 text-center">${i + 1}</span>
      <input type="text" data-motiv-nazev maxlength="80" placeholder="Motiv ${i + 1} (např. jméno)" value="${escapeHtml(m.nazev)}"
        class="block w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white" />
      <input type="number" data-motiv-kusu min="0" value="${m.pocetKusu}"
        class="block w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white text-right" />
      <button type="button" data-motiv-remove="${i}" aria-label="Odebrat motiv ${i + 1}" class="p-1.5 text-gray-400 hover:text-red-600">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>
    </div>
  `).join('');

  panel.classList.remove('hidden');
  pocetKusuInput.disabled = true;
  pocetKusuDropdownBtn?.classList.add('hidden');
  updateMotivyRozsahVisibility();
  updateMotivySum();
}

// „Motivy se liší" (jen obálka × obálka i vnitřek) má smysl jen u vazby s jinou
// obálkou/přebalem – u volných listů a vazeb bez oddělené obálky se skrývá.
function updateMotivyRozsahVisibility() {
  const wrapper = document.getElementById('motivy-rozsah-wrapper');
  if (!wrapper) return;
  const typ = document.getElementById('vazba')?.value || '0';
  const druhu = +(document.getElementById('pocetDruhu')?.value || 1);
  const jinaObalka = document.getElementById('vazbaJinaObalka')?.checked || typ === 'blokPrebal';
  wrapper.classList.toggle('hidden', !(typ !== '0' && druhu > 1 && jinaObalka));
}

function setupMotivyPanel() {
  const pocetDruhuInput = document.getElementById('pocetDruhu');
  const rowsBox = document.getElementById('motivy-rows');
  if (!pocetDruhuInput || !rowsBox) return;

  pocetDruhuInput.addEventListener('input', () => renderMotivyPanel());
  document.getElementById('vazba')?.addEventListener('change', updateMotivyRozsahVisibility);
  document.getElementById('vazbaJinaObalka')?.addEventListener('change', updateMotivyRozsahVisibility);

  // Suma průběžně při psaní (přepočet zajistí delegace change/keyup na formuláři)
  rowsBox.addEventListener('input', (e) => {
    if (e.target.matches('[data-motiv-kusu]')) updateMotivySum();
  });

  // Odebrání řádku
  rowsBox.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-motiv-remove]');
    if (!btn) return;
    const idx = +btn.getAttribute('data-motiv-remove');
    const values = getMotivyRows();
    values.splice(idx, 1);
    pocetDruhuInput.value = Math.max(1, values.length);
    renderMotivyPanel(values.length > 1 ? values : null);
    pocitej();
  });

  document.getElementById('motivy-add')?.addEventListener('click', () => {
    pocetDruhuInput.value = Math.max(2, (+pocetDruhuInput.value || 1) + 1);
    renderMotivyPanel();
    pocitej();
  });

  document.getElementById('motivy-rovnomerne')?.addEventListener('click', () => {
    const values = getMotivyRows();
    if (values.length < 2) return;
    const sum = values.reduce((a, m) => a + m.pocetKusu, 0);
    const base = Math.floor(sum / values.length);
    let zbytek = sum - base * values.length;
    const rozdelene = values.map((m) => ({ ...m, pocetKusu: base + (zbytek-- > 0 ? 1 : 0) }));
    renderMotivyPanel(rozdelene);
    pocitej();
  });
}

setupMotivyPanel();
window.getMotivyRows = getMotivyRows;
window.renderMotivyPanel = renderMotivyPanel;

// ============================================================================
// SKRÝVÁNÍ ROZKRESU DESEK
// Někoho zajímají jen textová data kalkulace – rozkres vyřazení na desky lze
// skrýt tlačítkem. Volba se pamatuje v sessionStorage a aplikuje se už při
// vykreslení (kvůli animaci výšky výsledku). Do technických listů pro výrobu
// se tlačítko nepropisuje – tam rozkres patří vždy.
// ============================================================================

const ROZKRES_STORAGE_KEY = 'tria-rozkres-desek-skryty';

function jeRozkresSkryty() {
  try { return sessionStorage.getItem(ROZKRES_STORAGE_KEY) === '1'; } catch { return false; }
}

function nastavRozkresSkryti(rozkresBoxEl, btnEl, skryt) {
  if (!rozkresBoxEl || !btnEl) return;
  rozkresBoxEl.style.display = skryt ? 'none' : '';
  btnEl.textContent = skryt ? 'Zobrazit rozkres vyřazení' : 'Skrýt rozkres vyřazení';
  btnEl.setAttribute('aria-expanded', skryt ? 'false' : 'true');
}

// Doplní do HTML z kalkulace tlačítko Skrýt/Zobrazit a případně rovnou skryje obsah.
function pripravRozkresHtml(html) {
  const str = String(html || '');
  if (!str.includes('data-rozkres-desek')) return str;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = str;

  const rozkresBox = wrapper.querySelector('[data-rozkres-desek]');
  if (!rozkresBox) return str;

  const controls = document.createElement('div');
  controls.className = 'mt-3 mb-2';
  controls.innerHTML = '<button type="button" data-rozkres-toggle class="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors">Skrýt rozkres vyřazení</button>';
  rozkresBox.parentNode?.insertBefore(controls, rozkresBox);

  const toggleBtn = controls.querySelector('[data-rozkres-toggle]');
  nastavRozkresSkryti(rozkresBox, toggleBtn, jeRozkresSkryty());

  return wrapper.innerHTML;
}

// Delegovaný klik – přežije každou výměnu obsahu #technicke při přepočtu.
document.getElementById('technicke')?.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-rozkres-toggle]');
  if (!btn) return;
  const rozkresBox = btn.closest('#technicke')?.querySelector('[data-rozkres-desek]');
  if (!rozkresBox) return;
  const maBytSkryty = rozkresBox.style.display !== 'none';
  nastavRozkresSkryti(rozkresBox, btn, maBytSkryty);
  try { sessionStorage.setItem(ROZKRES_STORAGE_KEY, maBytSkryty ? '1' : '0'); } catch { /* private mode */ }
});

// Doprava v "Dodání a fakturace" – možnosti a ceny se generují z Nastavení → Doprava.
function updateDopravaOptions() {
  const select = document.getElementById('doprava');
  if (!select) return;
  const g = (id, d) => {
    const el = document.getElementById(id);
    const v = el ? +el.value : NaN;
    return Number.isFinite(v) ? v : d;
  };
  const osobni = g('dopravaOsobni', 0);
  const zdarmaOd = g('dopravaZdarmaOd', 3000);
  const dpd = g('dopravaDpd', 100);
  const tria = g('dopravaTria', 0);
  const fmt = (c) => (c > 0 ? c + ' Kč' : 'zdarma');

  const prevIndex = select.selectedIndex;
  const options = [
    '<option value="0">Bez dopravy</option>',
    `<option value="${osobni}">Osobní vyzvednutí (${fmt(osobni)})</option>`,
    `<option value="0">DPD (zdarma nad ${zdarmaOd} Kč)</option>`,
    `<option value="${dpd}">DPD základní (${dpd} Kč)</option>`,
  ];
  for (let kg = 1; kg <= 7; kg++) {
    const cena = g(`dopravaDpdDo${kg}Kg`, 0);
    options.push(`<option value="${cena}">DPD do ${kg} kg (${cena} Kč)</option>`);
  }
  options.push(`<option value="${tria}">Doprava Tria (${fmt(tria)})</option>`);
  select.innerHTML = options.join('');
  if (prevIndex >= 0 && prevIndex < select.options.length) {
    select.selectedIndex = prevIndex;
  }
}

// Validace rozměrových limitů dokončovacích operací (falcování, bigování,
// výsek, předsek, ražba) podle Nastavení. Produkt smí být otočen o 90°.
function validateDokoncovaciLimity() {
  const box = document.getElementById('dokoncovaci-limity-error');
  if (!box) return;

  const sirka = +getValue('#produktRozmerSirka', 0);
  const vyska = +getValue('#produktRozmerVyska', 0);
  if (!sirka || !vyska) {
    box.classList.add('hidden');
    return;
  }

  const g = (id, d) => {
    const el = document.getElementById(id);
    const v = el ? +el.value : NaN;
    return Number.isFinite(v) ? v : d;
  };

  // [label, aktivní?, minX, minY, maxX, maxY]
  const operace = [
    ['Falcování', getSelectedValue('#falcovani', '0') !== '0',
      g('falcovaniRozmeryMinX', 148), g('falcovaniRozmeryMinY', 40), g('falcovaniRozmeryMaxZ', 353), g('falcovaniRozmeryMaxY', 500)],
    ['Bigování', getSelectedValue('#bigovani', '0') !== '0',
      g('bigovaniRozmeryMinX', 148), g('bigovaniRozmeryMinY', 40), g('bigovaniRozmeryMaxX', 353), g('bigovaniRozmeryMaxY', 500)],
    ['Výsek', getSelectedValue('#vysek', '0') !== '0',
      g('vysekRozmeryMinX', 0), g('vysekRozmeryMinY', 0), g('vysekRozmeryMaxX', 500), g('vysekRozmeryMaxY', 700)],
    ['Předsek', getSelectedValue('#predsek', '0') !== '0',
      g('predsekRozmeryMinX', 0), g('predsekRozmeryMinY', 0), g('predsekRozmeryMaxX', 500), g('predsekRozmeryMaxY', 700)],
    ['Ražba', getSelectedValue('#razba', '0') !== '0',
      g('razbaRozmeryMinX', 0), g('razbaRozmeryMinY', 0), g('razbaRozmeryMaxX', 350), g('razbaRozmeryMaxY', 500)],
  ];

  const fits = (a, b, minX, minY, maxX, maxY) => a >= minX && b >= minY && a <= maxX && b <= maxY;
  const chyby = [];
  for (const [label, aktivni, minX, minY, maxX, maxY] of operace) {
    if (!aktivni) continue;
    // Produkt lze do stroje vložit na šířku i na výšku
    if (!fits(sirka, vyska, minX, minY, maxX, maxY) && !fits(vyska, sirka, minX, minY, maxX, maxY)) {
      chyby.push(`${label}: rozměr ${sirka}×${vyska} mm je mimo povolený rozsah ${minX}×${minY} až ${maxX}×${maxY} mm.`);
    }
  }

  if (chyby.length) {
    box.innerHTML = '<strong>Překročené limity dokončovacích operací:</strong><br>' + chyby.join('<br>');
    box.classList.remove('hidden');
  } else {
    box.classList.add('hidden');
  }
}

// Změna cen dopravy v Nastavení se hned promítne do selectu Doprava
document.querySelectorAll('#dopravaOsobni, #dopravaDpd, #dopravaDpdDo1Kg, #dopravaDpdDo2Kg, #dopravaDpdDo3Kg, #dopravaDpdDo4Kg, #dopravaDpdDo5Kg, #dopravaDpdDo6Kg, #dopravaDpdDo7Kg, #dopravaTria, #dopravaZdarmaOd').forEach((el) => {
  el.addEventListener('change', updateDopravaOptions);
});

// Synchronizace nastavení s kalkulátorem
function synchronizeSettings() {
  const nastaveniForm = document.getElementById('nastaveni-form');
  
  if (nastaveniForm) {
    // Přidej event listener na všechny input prvky v formuláři nastavení
    // (kromě #technologie, které se ukládá lokálně do sessionStorage)
    const inputs = nastaveniForm.querySelectorAll('input, select');
    inputs.forEach(input => {
      if (input.id === 'technologie') return; // Technologie se neukládá na server
      input.addEventListener('change', async () => {
        // Uložit nastavení do API
        await saveSettings();
        
        // Při změně nastavení spusť znovu výpočet
        if (sectionManager.currentSection === 'kalkulator') {
          setTimeout(() => pocitej(), 100);
        }
      });
    });
  }
}

// Inicializace synchronizace nastavení
synchronizeSettings();

// Konečná inicializace po vytvoření všech objektů
document.addEventListener('DOMContentLoaded', async () => {
  // Počkej chvilku než se vytvoří všechny objekty
  await new Promise(resolve => setTimeout(resolve, 100));

  // loadSettings() se volá až po ověření session / loginu (unlockApp),
  // protože GET /api/settings vyžaduje přihlášení.

  // Přidat event listenery pro dropdown menu
  const settingsDropdownBtn = document.getElementById('settings-dropdown-btn');
  const materialsDropdownBtn = document.getElementById('materials-dropdown-btn');
  
  if (settingsDropdownBtn) {
    settingsDropdownBtn.addEventListener('click', toggleSettingsDropdown);
  }
  
  if (materialsDropdownBtn) {
    materialsDropdownBtn.addEventListener('click', toggleMaterialsDropdown);
  }
});

// Funkce pro ovládání dropdown menu
function toggleSettingsDropdown() {
  const dropdown = document.getElementById('settings-dropdown');
  const button = document.getElementById('settings-dropdown-btn');
  
  if (dropdown && button) {
    dropdown.classList.toggle('hidden');
  }
}

function toggleMaterialsDropdown() {
  const dropdown = document.getElementById('materials-dropdown');
  const button = document.getElementById('materials-dropdown-btn');
  
  if (dropdown && button) {
    dropdown.classList.toggle('hidden');
  }
}

// Zavřít dropdown menu při kliknutí mimo
document.addEventListener('click', function(event) {
  const settingsDropdown = document.getElementById('settings-dropdown');
  const materialsDropdown = document.getElementById('materials-dropdown');
  const settingsButton = document.getElementById('settings-dropdown-btn');
  const materialsButton = document.getElementById('materials-dropdown-btn');
  
  // Zavřít settings dropdown
  if (settingsDropdown && !event.target.closest('#settings-dropdown') && !event.target.closest('#settings-dropdown-btn')) {
    settingsDropdown.classList.add('hidden');
  }
  
  // Zavřít materials dropdown
  if (materialsDropdown && !event.target.closest('#materials-dropdown') && !event.target.closest('#materials-dropdown-btn')) {
    materialsDropdown.classList.add('hidden');
  }
});

// Funkce pro kopírování výsledku kalkulace
function copyCalculationResult() {
  const resultElement = document.getElementById('mail');
  if (!resultElement) return;
  
  // Získat textový obsah výsledku (bez HTML tagů)
  const textToCopy = resultElement.innerText || resultElement.textContent;
  
  if (!textToCopy || textToCopy.trim() === '') {
    showNotification('Žádný výsledek kalkulace ke kopírování', 'warning');
    return;
  }
  
  // Zkusit použít moderní Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(textToCopy).then(() => {
      showNotification('Výsledek kalkulace zkopírován do schránky', 'success');
    }).catch(err => {
      console.error('Chyba při kopírování:', err);
      fallbackCopyTextToClipboard(textToCopy);
    });
  } else {
    // Fallback pro starší prohlížeče
    fallbackCopyTextToClipboard(textToCopy);
  }
}

// Fallback funkce pro kopírování (pro starší prohlížeče)
function fallbackCopyTextToClipboard(text) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    const successful = document.execCommand('copy');
    if (successful) {
      showNotification('Výsledek kalkulace zkopírován do schránky', 'success');
    } else {
      showNotification('Nepodařilo se zkopírovat výsledek', 'error');
    }
  } catch (err) {
    console.error('Chyba při kopírování:', err);
    showNotification('Nepodařilo se zkopírovat výsledek', 'error');
  }
  
  document.body.removeChild(textArea);
}

// Funkce pro skrývání/zobrazování doplňkových inputů
function setupConditionalInputs() {
  // Výsek - skryj/zobraz "Pozic na raznici" a "Cena raznice"
  const vysekSelect = document.getElementById('vysek');
  const vysekInputsContainer = document.querySelector('.vysek-extra');
  
  // Předsek - skryj/zobraz "Pozic na raznici" a "Cena raznice"  
  const predsekSelect = document.getElementById('predsek');
  const predsekInputsContainer = document.querySelector('.predsek-extra');
  
  // Ražba - skryj/zobraz "Pozic na štočku" a "Cena štočku"
  const razbaSelect = document.getElementById('razba');
  const razbaInputsContainer = document.querySelector('.razba-extra');

  // Vazba - skryj/zobraz všechny vazebné inputy
  const vazbaSelect = document.getElementById('vazba');
  const vazbaInputsContainers = document.querySelectorAll('.vazba-inputs');

  // Lepení - skryj/zobraz "Počet lepení/kus"
  const lepeniSelect = document.getElementById('lepeni');
  const lepeniPocetContainer = document.getElementById('lepeniPocet')?.parentElement;

  function toggleInputsVisibility(selectElement, inputsContainer, defaultValue) {
    if (!selectElement || !inputsContainer) {
      console.warn('Element not found for conditional inputs:', selectElement, inputsContainer);
      return;
    }
    
    const isDefaultSelected = selectElement.value === defaultValue;
    
    if (isDefaultSelected) {
      inputsContainer.classList.add('hidden');
    } else {
      inputsContainer.classList.remove('hidden');
    }
  }

  function toggleMultipleInputsVisibility(selectElement, inputsContainers, defaultValue) {
    if (!selectElement || !inputsContainers || inputsContainers.length === 0) {
      console.warn('Element not found for conditional inputs:', selectElement, inputsContainers);
      return;
    }
    
    const isDefaultSelected = selectElement.value === defaultValue;
    
    inputsContainers.forEach(container => {
      if (isDefaultSelected) {
        container.classList.add('hidden');
      } else {
        container.classList.remove('hidden');
      }
    });
  }

  // Nastavit počáteční stav a event listenery
  if (vysekSelect && vysekInputsContainer) {
    toggleInputsVisibility(vysekSelect, vysekInputsContainer, '0');
    vysekSelect.addEventListener('change', () => {
      toggleInputsVisibility(vysekSelect, vysekInputsContainer, '0');
    });
  }

  if (predsekSelect && predsekInputsContainer) {
    toggleInputsVisibility(predsekSelect, predsekInputsContainer, '0');
    predsekSelect.addEventListener('change', () => {
      toggleInputsVisibility(predsekSelect, predsekInputsContainer, '0');
    });
  }

  if (razbaSelect && razbaInputsContainer) {
    toggleInputsVisibility(razbaSelect, razbaInputsContainer, '0');
    razbaSelect.addEventListener('change', () => {
      toggleInputsVisibility(razbaSelect, razbaInputsContainer, '0');
    });
  }

  if (lepeniSelect && lepeniPocetContainer) {
    toggleInputsVisibility(lepeniSelect, lepeniPocetContainer, '0');
    lepeniSelect.addEventListener('change', () => {
      toggleInputsVisibility(lepeniSelect, lepeniPocetContainer, '0');
    });
  }

  // Vazba - skrývání inputů při volbě "Bez vazby"
  if (vazbaSelect && vazbaInputsContainers.length > 0) {
    toggleMultipleInputsVisibility(vazbaSelect, vazbaInputsContainers, '0');
    vazbaSelect.addEventListener('change', () => {
      toggleMultipleInputsVisibility(vazbaSelect, vazbaInputsContainers, '0');
    });
  }

  // Vazba UI - info box, validace, toggle
  setupVazbaUI();
  setupVnitrniMaterialSync();
  setupPodlozkaMaterialSync();
  setupPriceCharts();
}

// ============================================================================
// Vývoj ceny podle počtu kusů – univerzální grafy (shared/price-chart.js)
// ============================================================================
// Grafy se načítají líně – až po kliknutí na tlačítko s ikonou grafu:
//  - kalkulátor (#pocetKusuChartBtn): kompletní kalkulace položky přes
//    POST /api/calculate/curve (vazba, dokončování, volba ofset/digitál)
//  - Nastavení ([data-price-chart]): křivky ceníků vazby počítané klientsky
//    ze vzorce a aktuálních hodnot formuláře

function setupPriceCharts() {
  document.getElementById('pocetKusuChartBtn')?.addEventListener('click', openCalculatorPriceChart);
  document.querySelectorAll('[data-price-chart]').forEach((btn) => {
    btn.addEventListener('click', () => openSettingsPriceChart(btn.dataset.priceChart));
  });
}

const CHART_SAMPLES = [100, 250, 500, 1000, 2000];

/** Graf ceníku v Nastavení – vzorce odpovídají backend/calc/calc-vazba.js. */
function openSettingsPriceChart(kind) {
  const v = (id, d) => +getValue('#' + id, d);

  if (kind === 'v1') {
    const stran = 32;
    const fnStrojni = (n) => v('vazbaV1StrojniPriprava', 200) + v('vazbaV1StrojniCenaZaKus', 0.6) * n;
    const fnRucni = (n) => v('vazbaV1RucniPriprava', 100) + v('vazbaV1RucniCenaZaKus', 1.6) * n
      + n * stran * v('vazbaV1RucniCenaZaStranu', 0.3) / 4;
    openPriceChartModal({
      title: 'V1 (šitá skobou) – vývoj ceny',
      subtitle: `Podle aktuálních hodnot v Nastavení · ruční šití počítáno při ${stran} stranách`,
      load: () => ({
        series: [
          { label: 'V1 strojní', points: pointsFromFn(fnStrojni, 2000) },
          { label: `V1 ručně (${stran} stran)`, points: pointsFromFn(fnRucni, 2000) }
        ]
      })
    });
    return;
  }

  if (kind === 'krouzek') {
    const stupne = [
      ['A7', 'do 105×74 mm'], ['A6', 'do 105×148 mm'], ['A5', 'do 210×148 mm'],
      ['A4', 'do 210×297 mm'], ['A3', 'do 420×594 mm']
    ];
    openPriceChartModal({
      title: 'Kroužková twin wire – vývoj ceny',
      subtitle: 'Podle aktuálních hodnot v Nastavení · jedna křivka na rozměrový stupeň',
      load: () => ({
        series: stupne.map(([kod, label]) => ({
          label: `${kod} (${label})`,
          points: pointsFromFn((n) => v(`vazbaKrouzekBlok${kod}Priprava`, 200) + v(`vazbaKrouzekBlok${kod}CenaZaKus`, 12) * n, 1000)
        }))
      })
    });
    return;
  }

  if (kind === 'kalendar') {
    const fn = (n) => v('vazbaKrouzekKalendarPriprava', 200) + v('vazbaKrouzekKalendarCenaZaKus', 14) * n;
    openPriceChartModal({
      title: 'Kroužková s háčkem – vývoj ceny',
      subtitle: 'Podle aktuálních hodnot v Nastavení',
      load: () => ({
        series: [{ label: 'Kroužková s háčkem', points: pointsFromFn(fn, 1000) }],
        chips: chipsFromFn(fn, [50, 100, 250, 500, 1000])
      })
    });
    return;
  }

  if (kind === 'v2pur') {
    const cenik = {
      priprava: v('vazbaV2PurPriprava', 400),
      kusStart: v('vazbaV2PurKusStart', 14),
      kusKonec: v('vazbaV2PurKusKonec', 8),
      prah: Math.max(v('vazbaV2PurPrah', 200), 1),
      utlum: Math.max(v('vazbaV2PurUtlum', 300), 1)
    };
    const fn = (n) => cenaMarginalni(n, cenik);
    const maxN = 2500;
    const asymptota = (n) => cenik.priprava + (cenik.kusStart - cenik.kusKonec) * (cenik.prah + cenik.utlum) + cenik.kusKonec * n;
    openPriceChartModal({
      title: 'V2 lepená (PUR) – vývoj ceny',
      subtitle: 'Podle aktuálních hodnot v Nastavení · přerušovaně: práh a asymptota koncové sazby',
      load: () => ({
        series: [{ label: 'V2 PUR', points: pointsFromFn(fn, maxN) }],
        markers: [{ x: cenik.prah, label: `práh ${cenik.prah.toLocaleString('cs-CZ')} ks` }],
        extraLines: [{ x1: cenik.prah, y1: asymptota(cenik.prah), x2: maxN, y2: asymptota(maxN) }],
        chips: chipsFromFn(fn, CHART_SAMPLES)
      })
    });
  }
}

/** Graf vývoje celkové ceny aktuální položky v kalkulátoru (počítá backend). */
function openCalculatorPriceChart() {
  const formData = gatherForm();
  const n0 = Math.max(1, Math.round(+formData.produkt.pocetKusu || 1));
  const mults = [0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 0.9, 1, 1.15, 1.35, 1.6, 2, 2.5, 3, 4, 5];
  const serie = [...new Set(mults.map((m) => Math.max(1, Math.round(n0 * m))))].sort((a, b) => a - b);
  const fmt = (val) => Math.round(val).toLocaleString('cs-CZ');

  openPriceChartModal({
    title: 'Vývoj ceny podle počtu kusů',
    subtitle: (formData.produkt.nazev ? `${formData.produkt.nazev} · ` : '')
      + 'kompletní kalkulace položky vč. dokončování a volby technologie',
    load: async () => {
      const response = await fetch(`${API_BASE_URL}/calculate/curve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, pocetKusuSerie: serie })
      });
      const json = await response.json();
      if (!json.success) throw new Error(json.error || 'Výpočet cenové křivky selhal');

      const pts = json.data.filter((p) => !p.error);
      if (!pts.length) {
        throw new Error((json.data.find((p) => p.error) || {}).error || 'Žádný platný bod křivky');
      }

      // Markery: aktuální náklad + místa, kde se mění technologie tisku
      const markers = [{ x: n0, label: `aktuálně ${fmt(n0)} ks` }];
      for (let i = 1; i < pts.length; i++) {
        if (pts[i].technologie && pts[i - 1].technologie && pts[i].technologie !== pts[i - 1].technologie) {
          markers.push({ x: pts[i].pocetKusu, label: `→ ${pts[i].technologie}` });
        }
      }

      // Čipy: rovnoměrný výběr max 6 bodů, aktuální náklad zvýrazněný
      const idx = new Set([0, pts.length - 1]);
      for (let k = 1; k <= 4; k++) idx.add(Math.round(k * (pts.length - 1) / 5));
      const chips = [...idx].sort((a, b) => a - b).map((i) => ({
        label: `${fmt(pts[i].pocetKusu)} ks`,
        value: `${fmt(pts[i].cenaCelkem)} Kč`,
        sub: `(${(pts[i].cenaCelkem / pts[i].pocetKusu).toFixed(2)} Kč/ks${pts[i].technologie ? ` · ${pts[i].technologie}` : ''})`,
        highlight: pts[i].pocetKusu === n0
      }));

      return {
        series: [{
          label: 'Cena celkem (bez DPH)',
          points: pts.map((p) => ({ x: p.pocetKusu, y: p.cenaCelkem, meta: { technologie: p.technologie } }))
        }],
        markers,
        chips
      };
    }
  });
}

// ============================================================================
// VAZBA UI - Info box, validace, toggle obálka/vnitřní blok
// ============================================================================

const VAZBA_CONFIG = {
  'V1-strojni': {
    nazev: 'V1 (2 skoby) strojní',
    minSirka: 99, minVyska: 148,
    maxSirka: 300, maxVyska: 300,
    maxStran: 72,
    orientace: 'delší i kratší strana',
    tisk: null,
    popis: null
  },
  'V1-rucni': {
    nazev: 'V1 (2 skoby) ručně',
    minSirka: 50, minVyska: 110,
    maxSirka: 220, maxVyska: 307,
    maxStran: 110,
    orientace: 'delší i kratší strana',
    tisk: null,
    popis: null
  },
  'blok': {
    nazev: 'Lepený blok',
    minSirka: 74, minVyska: 105,
    maxSirka: 210, maxVyska: 297,
    maxStran: null,
    orientace: null,
    tisk: null,
    popis: null
  },
  'blokPrebal': {
    nazev: 'Lepený blok s přebalem',
    minSirka: 74, minVyska: 105,
    maxSirka: 210, maxVyska: 297,
    maxStran: null,
    orientace: null,
    tisk: null,
    popis: null
  },
  'V2-PUR': {
    nazev: 'V2 lepená (PUR)',
    minSirka: 105, minVyska: 148,
    maxSirka: 210, maxVyska: 297,
    maxStran: null,
    orientace: null,
    tisk: null,
    popis: 'Hřbet: 4–30 mm'
  },
  'krouzek-blok': {
    nazev: 'Kroužková twin wire',
    minSirka: 74, minVyska: 105,
    maxSirka: 420, maxVyska: 594,
    maxStran: null,
    orientace: null,
    tisk: 'jednotlivé listy (ne přeložené)',
    popis: 'Cena vazby podle rozměru: do A7, A6, A5, A4, A3. Min. rozměr A7 (105×74 mm).'
  },
  'krouzek-kalendar': {
    nazev: 'Kroužková s háčkem na pověšení',
    minSirka: 74, minVyska: 105,
    maxSirka: 420, maxVyska: 594,
    maxStran: null,
    orientace: null,
    tisk: 'jednotlivé listy (ne přeložené)',
    popis: 'Včetně háčku na pověšení. Min. rozměr A7 (105×74 mm).'
  }
};

function setupVazbaUI() {
  const vazbaSelect = document.getElementById('vazba');
  const infoBox = document.getElementById('vazba-info-box');
  const maxStranHint = document.getElementById('vazba-max-stran-hint');
  const stranError = document.getElementById('vazba-stran-error');
  const rozmeryError = document.getElementById('vazba-rozmery-error');
  const pocetStranInput = document.getElementById('vazbaPocetStran');
  const pocetStranLabel = document.getElementById('vazba-pocet-stran-label');
  const pocetStranHelp = document.getElementById('vazba-pocet-stran-help');
  const vnitrniTiskSelect = document.getElementById('vazbaVnitrniTisk');
  const vnitrniTiskJinaOption = vnitrniTiskSelect?.querySelector('option[value="jina"]');
  const vnitrniTiskStejnaOption = vnitrniTiskSelect?.querySelector('option[value="stejna"]');
  const toggle = document.getElementById('vazbaJinaObalka');
  const obalkaZadaSelect = document.getElementById('vazbaObalkaZada');
  const jinaObalkaWrapper = document.getElementById('vazba-jina-obalka-wrapper');
  const vnitrniBlokSection = document.getElementById('vazba-vnitrni-blok-section');
  const obalkaTitle = document.getElementById('vazba-obalka-title');
  const podlozkaSection = document.getElementById('vazba-podlozka-section');

  if (!vazbaSelect || !infoBox) return;

  // Aktualizace info boxu podle zvoleného typu vazby
  function updateInfoBox() {
    const typ = vazbaSelect.value;
    if (typ === '0') {
      infoBox.classList.add('hidden');
      return;
    }

    const config = VAZBA_CONFIG[typ];
    if (!config) {
      infoBox.classList.add('hidden');
      return;
    }

    let html = '<div class="flex items-start">' + icon('information-circle', 'w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-amber-600') + '<div>';
    html += '<p class="font-semibold text-amber-800 mb-1">' + config.nazev + '</p>';
    html += '<p class="text-amber-700">Rozměry: ' + config.minSirka + '×' + config.minVyska + ' mm až ' + config.maxSirka + '×' + config.maxVyska + ' mm</p>';
    if (config.maxStran) {
      const jednotkyInfo = jednotkyVazby(typ);
      const vcetne = jednotkyInfo.unit === 'listy' ? '(včetně obálkových listů)' : '(včetně obálky)';
      html += '<p class="text-amber-700">Max. počet ' + jednotkyInfo.slovo + ': ' + config.maxStran + ' ' + vcetne + '</p>';
    }
    if (config.orientace) {
      html += '<p class="text-amber-700">Orientace: ' + config.orientace + '</p>';
    }
    if (config.tisk) {
      html += '<p class="text-amber-700">Tisk: ' + config.tisk + '</p>';
    }
    if (config.popis) {
      html += '<p class="text-amber-700">' + config.popis + '</p>';
    }
    html += '</div></div>';

    infoBox.innerHTML = html;
    infoBox.classList.remove('hidden');
  }

  // Validace rozměrů produktu vůči omezením vazby
  function validateDimensions() {
    const typ = vazbaSelect.value;
    if (typ === '0' || !rozmeryError) return;

    const config = VAZBA_CONFIG[typ];
    if (!config) {
      rozmeryError.classList.add('hidden');
      return;
    }

    const sirka = +(document.getElementById('produktRozmerSirka')?.value || 0);
    const vyska = +(document.getElementById('produktRozmerVyska')?.value || 0);

    if (sirka === 0 && vyska === 0) {
      rozmeryError.classList.add('hidden');
      return;
    }

    // Kontrola s flexibilní orientací (kratší strana = šířka, delší = výška)
    const prodMin = Math.min(sirka, vyska);
    const prodMax = Math.max(sirka, vyska);
    const vazbaMin = Math.min(config.minSirka, config.minVyska);
    const vazbaMax = Math.max(config.minSirka, config.minVyska);
    const vazbaMaxMin = Math.min(config.maxSirka, config.maxVyska);
    const vazbaMaxMax = Math.max(config.maxSirka, config.maxVyska);

    const tooSmall = prodMin < vazbaMin || prodMax < vazbaMax;
    const tooBig = prodMin > vazbaMaxMin || prodMax > vazbaMaxMax;

    if (tooSmall || tooBig) {
      rozmeryError.textContent = 'Rozměry ' + sirka + '×' + vyska + ' mm jsou mimo rozsah pro ' + config.nazev + ' (' + config.minSirka + '×' + config.minVyska + ' až ' + config.maxSirka + '×' + config.maxVyska + ' mm)';
      rozmeryError.classList.remove('hidden');
    } else {
      rozmeryError.classList.add('hidden');
    }
  }

  // Validace počtu stran
  function validatePages() {
    const typ = vazbaSelect.value;
    if (typ === '0') {
      if (maxStranHint) maxStranHint.textContent = '';
      if (stranError) stranError.classList.add('hidden');
      return;
    }

    const config = VAZBA_CONFIG[typ];
    if (!config) return;

    const pocetStran = +(pocetStranInput?.value || 0);

    if (config.maxStran && maxStranHint) {
      maxStranHint.textContent = '(max. ' + config.maxStran + ')';
    } else if (maxStranHint) {
      maxStranHint.textContent = '';
    }

    const slovo = jednotkyVazby(typ).slovo;
    const nasobekChyba = validaceNasobku(typ, pocetStran);
    if (config.maxStran && pocetStran > config.maxStran && stranError) {
      stranError.textContent = 'Maximální počet ' + slovo + ' pro ' + config.nazev + ' je ' + config.maxStran;
      stranError.classList.remove('hidden');
    } else if (nasobekChyba && stranError) {
      stranError.textContent = nasobekChyba;
      stranError.classList.remove('hidden');
    } else if (stranError) {
      stranError.classList.add('hidden');
    }

    updatePocetStranHelp(typ);
  }

  // Toggle: Jiná obálka než vnitřní blok
  function handleObalkaToggle() {
    if (!toggle || !vnitrniBlokSection || !obalkaTitle) return;
    const typ = vazbaSelect.value;
    const isBlok = typ === 'blok' || typ === 'blokPrebal';
    const isKrouzek = typ.startsWith('krouzek');

    if (toggle.checked) {
      vnitrniBlokSection.classList.remove('hidden');
      obalkaTitle.textContent = 'Obálka';
      // Naplnit cenu a gramáž z aktuálně vybrané option
      syncVnitrniMaterialInputs();
    } else {
      vnitrniBlokSection.classList.add('hidden');
      obalkaTitle.textContent = 'Obálka a vnitřní blok';
    }

    // Rozsah obálky (předek i záda / jen přední) – jen kroužková s jinou obálkou
    const obalkaZadaWrapper = document.getElementById('vazba-obalka-zada-wrapper');
    if (obalkaZadaWrapper) {
      const isKrouzekType = vazbaSelect.value.startsWith('krouzek');
      obalkaZadaWrapper.classList.toggle('hidden', !(isKrouzekType && toggle.checked));
    }
    updatePocetStranHelp(typ, isBlok, isKrouzek);
  }

  // Aktualizace UI pro vazbu:
  // - u lepených bloků ('blok', 'blokPrebal') je "Počet stran" ve skutečnosti počet listů
  // - přepínač "Jiná obálka" je dostupný pro všechny typy vazby kromě "Bez vazby"
  // - podložka (šedá lepenka) je dostupná pouze pro lepené bloky
  function updatePocetStranHelp(typ) {
    if (!pocetStranHelp) return;
    if (!typ || typ === '0') {
      pocetStranHelp.textContent = '';
      return;
    }

    const hasJinaObalka = !!toggle?.checked;
    const maObalkaZada = (obalkaZadaSelect?.value || 'ano') === 'ano';
    const hodnota = +(pocetStranInput?.value || 0);

    // Primárně živý přepočet zadaného čísla (strany ↔ listy/složky).
    // Zákazník i obchodník hned vidí, jestli aplikace chápe totéž co on.
    const prepocet = prepocetJednotek(typ, hodnota, {
      jinaObalka: hasJinaObalka,
      obalkaZada: maObalkaZada,
    });
    if (prepocet) {
      pocetStranHelp.textContent = prepocet;
      return;
    }

    // Fallback (prázdné pole / neplatný násobek): obecné vysvětlení jednotky
    const meta = jednotkyVazby(typ);
    if (meta.unit === 'listy') {
      pocetStranHelp.textContent = typ === 'blok' || typ === 'blokPrebal'
        ? 'Zadejte počet listů vnitřního bloku (1 list = 2 strany, líc a rub).'
        : 'Zadejte počet listů včetně obálkových (1 list = 2 strany, líc a rub).';
    } else if (meta.nasobek === 4) {
      pocetStranHelp.textContent = 'Zadejte počet stran včetně obálky – násobek 4 (1 přeložený list / složka = 4 strany).';
    } else {
      pocetStranHelp.textContent = 'Zadejte počet stran včetně obálky – násobek 2 (1 list = 2 strany).';
    }
  }

  function updateBlokUI() {
    const typ = vazbaSelect.value;
    const hasVazba = typ !== '0';
    const isBlok = typ === 'blok' || typ === 'blokPrebal';
    const isKrouzek = typ.startsWith('krouzek');

    // Popisek + step pole počtu stran/listů – jednotky dle oborového standardu
    // (stránkované publikace = strany, bloky/kroužkové = listy), viz jednotky.js
    const jednotky = jednotkyVazby(typ);
    if (pocetStranLabel) {
      pocetStranLabel.textContent = jednotky.label;
    }
    if (pocetStranInput) {
      pocetStranInput.setAttribute('min', String(jednotky.min));
      pocetStranInput.setAttribute('step', String(jednotky.step));
    }
    // Dropdown předvoleb mluví stejnou jednotkou jako label (stran/listů)
    document.querySelectorAll('#pocetStranDropdown .stran-preset').forEach((btn) => {
      btn.textContent = btn.getAttribute('data-value') + ' ' + jednotky.slovo;
    });
    if (vnitrniTiskJinaOption && vnitrniTiskStejnaOption) {
      if (isBlok || isKrouzek) {
        vnitrniTiskJinaOption.textContent = 'Každý list jiný (kalendář, blok)';
        vnitrniTiskStejnaOption.textContent = 'Každý list stejný (opakující se motiv)';
      } else {
        vnitrniTiskJinaOption.textContent = 'Každá strana jiná (brožura, katalog)';
        vnitrniTiskStejnaOption.textContent = 'Každá strana stejná (blok, sešit)';
      }
    }
    updatePocetStranHelp(typ, isBlok, isKrouzek);

    // Barva spirály – jen pro kroužkové vazby
    const barvaSpiralyWrapper = document.getElementById('vazba-barva-spiraly-wrapper');
    if (barvaSpiralyWrapper) {
      barvaSpiralyWrapper.classList.toggle('hidden', !isKrouzek);
    }

    // „Jiná obálka" je dostupná pro všechny typy vazby (mimo "Bez vazby").
    if (jinaObalkaWrapper) {
      if (hasVazba) {
        jinaObalkaWrapper.classList.remove('hidden');
      } else {
        jinaObalkaWrapper.classList.add('hidden');
        if (toggle) toggle.checked = false;
      }
    }
    // Bez vazby vždy schovat sekci vnitřního bloku.
    if (!hasVazba && vnitrniBlokSection) {
      vnitrniBlokSection.classList.add('hidden');
    }

    // Podložka – pro lepené bloky a kroužkové vazby.
    if (podlozkaSection) {
      if (isBlok || isKrouzek) {
        podlozkaSection.classList.remove('hidden');
      } else {
        podlozkaSection.classList.add('hidden');
        const podlozkaToggle = document.getElementById('vazbaPodlozka');
        if (podlozkaToggle) podlozkaToggle.checked = false;
        const materialSection = document.getElementById('vazba-podlozka-material-section');
        if (materialSection) materialSection.classList.add('hidden');
      }
    }

    handleObalkaToggle();
  }

  // Event listenery
  vazbaSelect.addEventListener('change', () => {
    updateInfoBox();
    updateBlokUI();
    validateDimensions();
    validatePages();
  });

  if (pocetStranInput) {
    pocetStranInput.addEventListener('input', validatePages);
    pocetStranInput.addEventListener('change', validatePages);
  }

  // Validace rozměrů při změně šířky/výšky
  const sirkaInput = document.getElementById('produktRozmerSirka');
  const vyskaInput = document.getElementById('produktRozmerVyska');
  if (sirkaInput) {
    sirkaInput.addEventListener('input', validateDimensions);
    sirkaInput.addEventListener('change', validateDimensions);
  }
  if (vyskaInput) {
    vyskaInput.addEventListener('input', validateDimensions);
    vyskaInput.addEventListener('change', validateDimensions);
  }

  // Validace rozměrů při změně formátu
  const formatSelect = document.getElementById('produktRozmerFormat');
  if (formatSelect) {
    formatSelect.addEventListener('change', () => {
      // Počkej chvíli než se aktualizují rozměry
      setTimeout(validateDimensions, 50);
    });
  }

  if (toggle) {
    toggle.addEventListener('change', handleObalkaToggle);
  }

  // Glosář jednotek (strana / list / složka / arch) pod ikonou ⓘ u labelu
  const glosarBtn = document.getElementById('vazba-jednotky-info-btn');
  const glosar = document.getElementById('vazba-jednotky-glosar');
  if (glosarBtn && glosar) {
    glosarBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      glosar.classList.toggle('hidden');
    });
    document.getElementById('vazba-jednotky-glosar-zavrit')?.addEventListener('click', () => glosar.classList.add('hidden'));
    document.addEventListener('click', (e) => {
      if (!glosar.classList.contains('hidden') && !glosar.contains(e.target) && !glosarBtn.contains(e.target)) {
        glosar.classList.add('hidden');
      }
    });
  }
  if (obalkaZadaSelect) {
    obalkaZadaSelect.addEventListener('change', () => {
      const typ = vazbaSelect.value;
      const isBlok = typ === 'blok' || typ === 'blokPrebal';
      const isKrouzek = typ.startsWith('krouzek');
      updatePocetStranHelp(typ, isBlok, isKrouzek);
    });
  }

  // Inicializace stavu
  updateInfoBox();
  updateBlokUI();
  validateDimensions();
  validatePages();
  handleObalkaToggle();
}

// Synchronizace materiálů do vnitřního bloku selectu
function syncVnitrniMaterialSelect() {
  const mainSelect = document.getElementById('material');
  const vnitrniSelect = document.getElementById('vnitrniMaterial');
  if (!mainSelect || !vnitrniSelect) return;

  // Uložit aktuální výběr
  const currentValue = vnitrniSelect.value;

  // Zkopírovat obsah z hlavního selectu
  vnitrniSelect.innerHTML = mainSelect.innerHTML;

  // Obnovit výběr pokud možno
  if (currentValue) {
    const option = vnitrniSelect.querySelector('option[value="' + currentValue + '"]');
    if (option) {
      option.selected = true;
    }
  }

  // Synchronizovat cenu a gramáž z aktuálně vybrané option do inputů
  syncVnitrniMaterialInputs();
}

// Přetáhne cenu a gramáž z vybrané option vnitřního materiálu do inputů
function syncVnitrniMaterialInputs() {
  const vnitrniSelect = document.getElementById('vnitrniMaterial');
  const vnitrniCena = document.getElementById('vnitrniMaterialCena');
  const vnitrniGramaz = document.getElementById('vnitrniMaterialGramaz');

  if (!vnitrniSelect || !vnitrniCena || !vnitrniGramaz) return;
  if (vnitrniSelect.value === 'vlastni') return;

  const selectedOption = vnitrniSelect.options[vnitrniSelect.selectedIndex];
  if (selectedOption && selectedOption.value) {
    vnitrniCena.value = selectedOption.value;
    vnitrniGramaz.value = selectedOption.getAttribute('data-gramaz') || '0';
  }
}

// Event listener pro synchronizaci ceny/gramáže při změně vnitřního materiálu
function setupVnitrniMaterialSync() {
  const vnitrniSelect = document.getElementById('vnitrniMaterial');
  const vnitrniCena = document.getElementById('vnitrniMaterialCena');
  const vnitrniGramaz = document.getElementById('vnitrniMaterialGramaz');

  if (!vnitrniSelect || !vnitrniCena || !vnitrniGramaz) return;

  vnitrniSelect.addEventListener('change', () => {
    if (vnitrniSelect.value === 'vlastni') return;

    const selectedOption = vnitrniSelect.options[vnitrniSelect.selectedIndex];
    if (selectedOption) {
      vnitrniCena.value = selectedOption.value;
      vnitrniGramaz.value = selectedOption.getAttribute('data-gramaz') || '0';
    }
  });

  // Při ručním zadání ceny/gramáže přepnout na "Vlastní materiál"
  [vnitrniCena, vnitrniGramaz].forEach(input => {
    input.addEventListener('keyup', () => { vnitrniSelect.value = 'vlastni'; });
    input.addEventListener('change', () => { vnitrniSelect.value = 'vlastni'; });
  });
}

// Synchronizace materiálů do selectu podložky (kopie z hlavního selectu)
function syncPodlozkaMaterialSelect() {
  const mainSelect = document.getElementById('material');
  const podlozkaSelect = document.getElementById('podlozkaMaterial');
  if (!mainSelect || !podlozkaSelect) return;

  const currentValue = podlozkaSelect.value;
  podlozkaSelect.innerHTML = mainSelect.innerHTML;
  if (currentValue) {
    const option = podlozkaSelect.querySelector('option[value="' + currentValue + '"]');
    if (option) option.selected = true;
  }
  syncPodlozkaMaterialInputs();
}

// Přetáhne cenu a gramáž z vybrané option podložky do inputů
function syncPodlozkaMaterialInputs() {
  const podlozkaSelect = document.getElementById('podlozkaMaterial');
  const podlozkaCena = document.getElementById('podlozkaMaterialCena');
  const podlozkaGramaz = document.getElementById('podlozkaMaterialGramaz');
  if (!podlozkaSelect || !podlozkaCena || !podlozkaGramaz) return;
  if (podlozkaSelect.value === 'vlastni') return;
  const selectedOption = podlozkaSelect.options[podlozkaSelect.selectedIndex];
  if (selectedOption && selectedOption.value) {
    podlozkaCena.value = selectedOption.value;
    podlozkaGramaz.value = selectedOption.getAttribute('data-gramaz') || '0';
  }
}

// Event listener pro synchronizaci ceny/gramáže podložky
function setupPodlozkaMaterialSync() {
  const podlozkaSelect = document.getElementById('podlozkaMaterial');
  const podlozkaCena = document.getElementById('podlozkaMaterialCena');
  const podlozkaGramaz = document.getElementById('podlozkaMaterialGramaz');
  if (!podlozkaSelect || !podlozkaCena || !podlozkaGramaz) return;

  podlozkaSelect.addEventListener('change', () => {
    if (podlozkaSelect.value === 'vlastni') return;
    const selectedOption = podlozkaSelect.options[podlozkaSelect.selectedIndex];
    if (selectedOption) {
      podlozkaCena.value = selectedOption.value;
      podlozkaGramaz.value = selectedOption.getAttribute('data-gramaz') || '0';
    }
  });

  [podlozkaCena, podlozkaGramaz].forEach(input => {
    input.addEventListener('keyup', () => { podlozkaSelect.value = 'vlastni'; });
    input.addEventListener('change', () => { podlozkaSelect.value = 'vlastni'; });
  });

  // Toggle podložky – zobrazit/skrýt materiál (přepočet řeší delegovaný listener na #vypocet)
  const toggle = document.getElementById('vazbaPodlozka');
  const materialSection = document.getElementById('vazba-podlozka-material-section');
  if (toggle && materialSection) {
    toggle.addEventListener('change', () => {
      if (toggle.checked) {
        materialSection.classList.remove('hidden');
        syncPodlozkaMaterialInputs();
      } else {
        materialSection.classList.add('hidden');
      }
    });
  }
}

// Přidat funkce do window objektu pro dostupnost z HTML onclick
window.toggleSettingsDropdown = toggleSettingsDropdown;
window.toggleMaterialsDropdown = toggleMaterialsDropdown;
window.copyCalculationResult = copyCalculationResult;

// ──────────────────────────────────────────────
// Quick presets – rychlé výchozí konfigurace pro běžné typy zakázek
// ──────────────────────────────────────────────
// Pozn. k `materialName` / `vnitrniMaterialName`:
// Preset vybere konkrétní materiál podle přesného názvu v selectu (option.textContent).
// Po výběru materiálu pocitej() automaticky doplní Kč/arch a g/m² z data atributů option.
const QUICK_PRESETS = [
  {
    id: 'vizitky',
    label: 'Vizitky',
    sub: '100 ks · 90×50 · 4/4 · Lesklá křída 350 g/m²',
    fields: { pocetKusu: 100, produktRozmerFormat: 'Vizitka', produktRozmerSirka: 90, produktRozmerVyska: 50, pocetBarev: '4/4', materialName: 'Lesklá křída 350 g/m²', vazba: '0' },
  },
  {
    id: 'letakA5',
    label: 'Letáky A5',
    sub: '500 ks · 4/4 · Lesklá křída 135 g/m²',
    fields: { pocetKusu: 500, produktRozmerFormat: 'A5', produktRozmerSirka: 148, produktRozmerVyska: 210, pocetBarev: '4/4', materialName: 'Lesklá křída 135 g/m²', vazba: '0' },
  },
  {
    id: 'lepenyBlokObalka',
    label: 'Lep. blok + obálka',
    sub: '200 ks · A5 · 60 listů · 4/0 + 1/1',
    // Pořadí matters: vazba před vazbaJinaObalka (toggle se objeví až po nastavení vazby)
    fields: {
      pocetKusu: 200,
      produktRozmerFormat: 'A5',
      produktRozmerSirka: 148,
      produktRozmerVyska: 210,
      vazba: 'blokPrebal',
      vazbaPocetStran: 60, // 60 listů vnitřního bloku
      vazbaJinaObalka: true,
      // Vnitřní blok – obyčejný papír, oboustranně černobíle
      vnitrniMaterialName: 'Ofset 80 g/m²',
      vnitrniBarevnost: '1/1',
      // Obálka (přebal) – silnější křída, plnobarevně z jedné strany
      materialName: 'Matná křída 250 g/m²',
      pocetBarev: '4/0',
      // Podložka (šedá lepenka) pod blok
      vazbaPodlozka: true,
      podlozkaMaterialName: 'Šedá lepenka 300 g/m²',
    },
  },
  {
    id: 'plakatA3',
    label: 'Plakáty A3',
    sub: '100 ks · 4/0 · Matná křída 170 g/m²',
    fields: { pocetKusu: 100, produktRozmerFormat: 'A3', produktRozmerSirka: 297, produktRozmerVyska: 420, pocetBarev: '4/0', materialName: 'Matná křída 170 g/m²', vazba: '0' },
  },
  {
    id: 'brozuraA5',
    label: 'Brožura A5',
    sub: '60 stran · V1 · 4/4 · Matná křída 115 g/m²',
    fields: { pocetKusu: 100, produktRozmerFormat: 'A5', produktRozmerSirka: 148, produktRozmerVyska: 210, pocetBarev: '4/4', materialName: 'Matná křída 115 g/m²', vazba: 'V1-strojni', vazbaPocetStran: 60 },
  },
  {
    id: 'obalkaDL',
    label: 'Obálka DL',
    sub: '500 ks · 1/0 · Ofset 90 g/m²',
    fields: { pocetKusu: 500, produktRozmerFormat: 'DL', produktRozmerSirka: 110, produktRozmerVyska: 220, pocetBarev: '1/0', materialName: 'Ofset 90 g/m²', vazba: '0' },
  },
];

// ──────────────────────────────────────────────
// Uživatelské předvolby – storage a helpers
// ──────────────────────────────────────────────
const PRESETS_STORAGE_KEY = 'tria:custom-presets';

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Načte pracovní sadu předvoleb z localStorage, nebo ji nasází z defaultů. */
function loadPresets() {
  try {
    const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) {}
  const seeded = QUICK_PRESETS.map((p) => ({ ...p, fields: { ...p.fields } }));
  savePresets(seeded);
  return seeded;
}

/** Uloží pracovní sadu předvoleb do localStorage. */
function savePresets(presets) {
  try {
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(presets));
  } catch (_) {}
}

/** Pracovní sada předvoleb (defaultní + uživatelské, bez vizuálního rozlišení). */
let userPresets = loadPresets();

/** Vygeneruje popisek chipu z polí předvolby: [ks/stran] · [barevnost] · [papír]. */
function generatePresetSub(fields) {
  const parts = [];
  const hasVazba = fields.vazba && fields.vazba !== '0';
  if (Array.isArray(fields.motivy) && fields.motivy.length > 1) {
    const sum = fields.motivy.reduce((a, m) => a + (+m.pocetKusu || 0), 0);
    parts.push(`${sum} ks (${fields.motivy.length} motivů)`);
  } else if (hasVazba && fields.vazbaPocetStran) {
    // Lepené bloky a kroužkové vazby počítají listy, ostatní vazby strany.
    const jsouListy = fields.vazba === 'blok' || fields.vazba === 'blokPrebal' || fields.vazba.startsWith('krouzek');
    parts.push(`${fields.vazbaPocetStran} ${jsouListy ? 'listů' : 'stran'}`);
  } else if (fields.pocetKusu) {
    parts.push(`${fields.pocetKusu} ks`);
  }
  if (fields.pocetBarev) parts.push(fields.pocetBarev);
  if (fields.materialName && fields.materialName !== 'vlastni') {
    parts.push(fields.materialName);
  }
  return parts.join(' · ');
}

/** Načte aktuální stav formuláře do objektu fields pro uložení předvolby. */
function captureFormFields() {
  const fields = {};
  const form = document.getElementById('vypocet');
  if (!form) return fields;

  // Materiály uložíme podle názvu (aby applyPreset našel správnou option i při
  // duplicitních cenách). Pro "vlastni" uložíme i cenu/gramáž.
  const materialSelect = document.getElementById('material');
  if (materialSelect) {
    const opt = materialSelect.options[materialSelect.selectedIndex];
    if (opt && opt.value && opt.value !== 'vlastni') {
      fields.materialName = opt.textContent.trim();
    } else {
      fields.materialName = 'vlastni';
      const cena = document.getElementById('materialCena');
      const gramaz = document.getElementById('materialGramaz');
      if (cena && cena.value) fields.materialCena = cena.value;
      if (gramaz && gramaz.value) fields.materialGramaz = gramaz.value;
    }
  }
  const vnitrniSelect = document.getElementById('vnitrniMaterial');
  if (vnitrniSelect) {
    const opt = vnitrniSelect.options[vnitrniSelect.selectedIndex];
    if (opt && opt.value && opt.value !== 'vlastni') {
      fields.vnitrniMaterialName = opt.textContent.trim();
    }
  }
  const podlozkaSelect = document.getElementById('podlozkaMaterial');
  if (podlozkaSelect) {
    const opt = podlozkaSelect.options[podlozkaSelect.selectedIndex];
    if (opt && opt.value && opt.value !== 'vlastni') {
      fields.podlozkaMaterialName = opt.textContent.trim();
    }
  }

  // Všechna ostatní pole s id (vynecháme materiály zpracované výše).
  const skipIds = new Set(['material', 'vnitrniMaterial', 'podlozkaMaterial', 'materialCena', 'materialGramaz']);
  form.querySelectorAll('input, select, textarea').forEach((el) => {
    if (!el.id || skipIds.has(el.id)) return;
    if (el.type === 'checkbox' || el.type === 'radio') {
      // Ukládáme i false – applyPreset pak checkbox spolehlivě vypne
      // (jinak by v něm zůstal stav z předchozí kalkulace).
      fields[el.id] = el.checked;
    } else if (el.value !== '' && el.value !== undefined) {
      fields[el.id] = el.value;
    }
  });

  // Motivy s vlastními náklady (dynamické řádky nemají id – serializují se zvlášť)
  if (+(fields.pocetDruhu || 1) > 1) {
    const rows = getMotivyRows().filter((m) => m.pocetKusu > 0);
    if (rows.length > 1) fields.motivy = rows;
  }

  // Bez vazby ("Volné listy") nemají vazbová pole význam – skryté inputy si
  // ale drží poslední hodnoty (např. počet stran z předchozí kalkulace),
  // takže by se do předvolby uložil zavádějící stav.
  if (!fields.vazba || fields.vazba === '0') {
    for (const id of Object.keys(fields)) {
      // 'vazba' samotnou zachováme, aby applyPreset vrátil select na "Volné listy".
      if (id === 'vazba') continue;
      if (id.startsWith('vazba') || id.startsWith('vnitrni') || id.startsWith('podlozka')) {
        delete fields[id];
      }
    }
  }
  return fields;
}

function setupQuickPresets() {
  const container = document.getElementById('quick-presets');
  if (!container) return;

  const render = () => {
    container.innerHTML = userPresets.map(p => `
      <button type="button" class="preset-chip group" data-preset-id="${p.id}">
        <span class="preset-chip-label">${escapeHtml(p.label)}</span>
        <span class="preset-chip-sub">${escapeHtml(p.sub)}</span>
      </button>
    `).join('');
  };
  render();

  container.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-preset-id]');
    if (!btn) return;
    const preset = userPresets.find(p => p.id === btn.dataset.presetId);
    if (!preset) return;
    applyPreset(preset);
    container.querySelectorAll('.preset-chip').forEach(c => c.classList.remove('is-active'));
    btn.classList.add('is-active');
  });

  setupPresetSaveModal(render);
  setupPresetManageModal(render);
}

// ──────────────────────────────────────────────
// Modal: Uložit aktuální jako předvolbu
// ──────────────────────────────────────────────
function setupPresetSaveModal(renderChips) {
  const modal = document.getElementById('presetSaveModal');
  const openBtn = document.getElementById('presetSaveBtn');
  const nameInput = document.getElementById('presetSaveName');
  const confirmBtn = document.getElementById('presetSaveConfirmBtn');
  const errorEl = document.getElementById('presetSaveError');
  if (!modal || !openBtn || !nameInput || !confirmBtn) return;

  const close = () => {
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    nameInput.value = '';
    if (errorEl) { errorEl.textContent = ''; errorEl.classList.add('hidden'); }
  };
  const open = () => {
    // Předvyplnit název odvozeným popiskem z aktuálních polí.
    const fields = captureFormFields();
    nameInput.value = generatePresetSub(fields);
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    setTimeout(() => nameInput.focus(), 0);
  };

  openBtn.addEventListener('click', open);
  modal.querySelectorAll('[data-preset-save-close]').forEach((el) => {
    el.addEventListener('click', close);
  });
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); confirmBtn.click(); }
    if (e.key === 'Escape') close();
  });

  confirmBtn.addEventListener('click', () => {
    const label = nameInput.value.trim();
    if (!label) {
      if (errorEl) {
        errorEl.textContent = 'Zadejte název předvolby.';
        errorEl.classList.remove('hidden');
      }
      nameInput.focus();
      return;
    }
    const fields = captureFormFields();
    const preset = {
      id: `user-${Date.now()}`,
      label,
      sub: generatePresetSub(fields),
      fields,
    };
    userPresets.push(preset);
    savePresets(userPresets);
    renderChips();
    close();
    if (typeof showNotification === 'function') {
      showNotification(`Předvolba „${label}" uložena.`, 'success');
    }
  });
}

// ──────────────────────────────────────────────
// Modal: Spravovat předvolby
// ──────────────────────────────────────────────
function setupPresetManageModal(renderChips) {
  const modal = document.getElementById('presetManageModal');
  const openBtn = document.getElementById('presetManageBtn');
  const listEl = document.getElementById('presetManageList');
  const emptyEl = document.getElementById('presetManageEmpty');
  const restoreBtn = document.getElementById('presetRestoreDefaultsBtn');
  if (!modal || !openBtn || !listEl) return;

  const close = () => {
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
  };
  const open = () => {
    renderList();
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
  };

  function renderItemHtml(p) {
    return `
      <li class="flex items-center gap-3 py-2.5" data-preset-id="${p.id}">
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-ink truncate">${escapeHtml(p.label)}</div>
          <div class="text-xs text-ink-500 truncate">${escapeHtml(p.sub)}</div>
        </div>
        <button type="button" class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" data-preset-action="rename" title="Přejmenovat">
          ${typeof icon === 'function' ? icon('pencil-square', 'w-5 h-5') : ''}
        </button>
        <button type="button" class="p-1 text-gray-400 hover:text-red-600 focus:outline-none" data-preset-action="delete" title="Smazat">
          ${typeof icon === 'function' ? icon('trash', 'w-5 h-5') : ''}
        </button>
      </li>
    `;
  }

  function renderList() {
    if (!userPresets.length) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.classList.remove('hidden');
      return;
    }
    if (emptyEl) emptyEl.classList.add('hidden');
    listEl.innerHTML = userPresets.map(renderItemHtml).join('');
  }

  /** Překreslí jen jeden řádek zpět do display módu (ostatní řádky neruší). */
  function renderRowDisplay(idx) {
    const p = userPresets[idx];
    if (!p) return;
    const li = listEl.querySelector(`li[data-preset-id="${CSS.escape(p.id)}"]`);
    if (!li) { renderList(); return; }
    li.outerHTML = renderItemHtml(p);
  }

  openBtn.addEventListener('click', open);
  modal.querySelectorAll('[data-preset-manage-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  function startRename(li, idx) {
    const current = userPresets[idx].label;
    li.innerHTML = `
      <div class="flex-1 min-w-0">
        <input type="text" class="block w-full px-2 py-1 sm:text-sm border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" value="${escapeHtml(current)}" maxlength="40" data-preset-rename-input />
      </div>
      <button type="button" class="p-1 text-green-600 hover:text-green-800 focus:outline-none" data-preset-rename-save title="Uložit">
        ${icon('check', 'w-5 h-5')}
      </button>
      <button type="button" class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" data-preset-rename-cancel title="Zrušit">
        ${icon('x-mark', 'w-5 h-5')}
      </button>
    `;
    const input = li.querySelector('[data-preset-rename-input]');
    input.focus();
    input.select();
    const commit = () => {
      const trimmed = input.value.trim();
      if (trimmed && trimmed !== current) {
        userPresets[idx].label = trimmed;
        savePresets(userPresets);
        renderChips();
      }
      renderRowDisplay(idx);
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { e.preventDefault(); renderRowDisplay(idx); }
    });
    li.querySelector('[data-preset-rename-save]').addEventListener('click', commit);
    li.querySelector('[data-preset-rename-cancel]').addEventListener('click', () => renderRowDisplay(idx));
  }

  listEl.addEventListener('click', (e) => {
    const actionBtn = e.target.closest('[data-preset-action]');
    if (!actionBtn) return;
    const li = actionBtn.closest('[data-preset-id]');
    if (!li) return;
    const id = li.dataset.presetId;
    const idx = userPresets.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const action = actionBtn.dataset.presetAction;

    if (action === 'delete') {
      const label = userPresets[idx].label;
      userPresets.splice(idx, 1);
      savePresets(userPresets);
      renderChips();
      renderList();
      if (typeof showNotification === 'function') {
        showNotification(`Předvolba „${label}" smazána.`, 'info');
      }
    } else if (action === 'rename') {
      startRename(li, idx);
    }
  });

  restoreBtn.addEventListener('click', () => {
    if (!window.confirm('Obnovit výchozí předvolby? Stávající seznam bude nahrazen defaultní sadou.')) return;
    userPresets = QUICK_PRESETS.map((p) => ({ ...p, fields: { ...p.fields } }));
    savePresets(userPresets);
    renderChips();
    renderList();
    if (typeof showNotification === 'function') {
      showNotification('Výchozí předvolby obnoveny.', 'success');
    }
  });
}

function applyPreset(preset) {
  // Vypnout "reset-na-vlastní" listenery, aby změna šířky/výšky nepřepsala
  // Formát na "vlastni" a změna gramáže/ceny nepřepsala Materiál na "vlastni".
  window._applyingPreset = true;
  try {
    Object.entries(preset.fields).forEach(([id, value]) => {
      // Speciální klíče pro výběr materiálu podle názvu – zpracují se níže
      // až po nastavení ostatních polí (aby je nepřepsalo "reset-na-vlastní").
      if (id === 'materialName' || id === 'vnitrniMaterialName' || id === 'podlozkaMaterialName') return;

      const el = document.getElementById(id);
      if (!el) return;
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = Boolean(value);
      } else {
        el.value = value;
      }
      // Trigger change event aby se spustily i conditional inputs / dependentní logika
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Obnova motivů (dynamické řádky) – až po nastavení pocetDruhu generickou smyčkou.
    if (Array.isArray(preset.fields.motivy) && preset.fields.motivy.length > 1) {
      const druhuInput = document.getElementById('pocetDruhu');
      if (druhuInput) druhuInput.value = preset.fields.motivy.length;
      renderMotivyPanel(preset.fields.motivy);
    } else {
      renderMotivyPanel();
    }

    // Nastavení materiálu podle názvu (až po ostatních polích).
    // Po výběru option v selectu následný pocitej() doplní #materialCena a #materialGramaz
    // z data-atributů vybrané option.
    if (preset.fields.materialName) {
      if (preset.fields.materialName === 'vlastni') {
        const select = document.getElementById('material');
        if (select) {
          select.value = 'vlastni';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        selectMaterialByName('material', preset.fields.materialName);
      }
    }
    if (preset.fields.vnitrniMaterialName) {
      if (preset.fields.vnitrniMaterialName === 'vlastni') {
        const select = document.getElementById('vnitrniMaterial');
        if (select) {
          select.value = 'vlastni';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        selectMaterialByName('vnitrniMaterial', preset.fields.vnitrniMaterialName);
      }
    }
    if (preset.fields.podlozkaMaterialName) {
      if (preset.fields.podlozkaMaterialName === 'vlastni') {
        const select = document.getElementById('podlozkaMaterial');
        if (select) {
          select.value = 'vlastni';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        selectMaterialByName('podlozkaMaterial', preset.fields.podlozkaMaterialName);
      }
    }
  } finally {
    window._applyingPreset = false;
  }

  // Načíst název předvolby do pole "Název položky" – propíše se do názvu položky
  // nabídky (OfferItemsManager.generateItemName ho používá jako prefix). Umožní
  // skládat nabídku z předvoleb jen klikáním a mít v ní názvy produktů.
  if (preset.label) {
    const nazevInput = document.getElementById('nazev');
    if (nazevInput) {
      nazevInput.value = preset.label;
      nazevInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  // Po nastavení polí proveď okamžitý přepočet – vyplní Kč/arch a g/m² z vybrané option.
  if (typeof pocitej === 'function') pocitej();
}

// Najde v daném <select> element <option>, jehož textContent odpovídá zadanému názvu,
// a nastaví ho jako vybraný. Používá se pro rychlé předvolby (quick presets).
function selectMaterialByName(selectId, name) {
  const select = document.getElementById(selectId);
  if (!select) return false;
  const trimmedName = String(name).trim();
  const option = Array.from(select.querySelectorAll('option')).find(
    (o) => o.textContent.trim() === trimmedName
  );
  if (!option) {
    console.warn(`[preset] Materiál "${trimmedName}" nebyl nalezen v selectu #${selectId}.`);
    return false;
  }
  // Pozn.: option.value je cena materiálu a v datech se ceny mohou opakovat
  // (různé papíry se stejnou cenou). Nastavení `select.value` by vybralo první
  // option s danou cenou, proto označíme přímo nalezenou option.
  select.querySelectorAll('option').forEach((o) => { o.selected = false; });
  option.selected = true;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

// Vystaveno na window pro offer-items.js (restore stavu formuláře při přepnutí položky).
window.selectMaterialByName = selectMaterialByName;
window.pocitej = pocitej;

// ──────────────────────────────────────────────
// Progressive disclosure – sbalitelné sekce (např. Dokončování)
// Použití: <div class="collapsible-section" data-collapsed="true|false">
//   <button class="collapsible-toggle">…</button>
//   <div class="collapsible-content">…</div>
// </div>
// ──────────────────────────────────────────────
const COLLAPSIBLE_STORAGE_PREFIX = 'tria:collapsible:';

function getCollapsibleStorageKey(section, index) {
  const id = section.id || section.dataset.collapsibleKey;
  if (id) return `${COLLAPSIBLE_STORAGE_PREFIX}${id}`;
  return `${COLLAPSIBLE_STORAGE_PREFIX}section-${index}`;
}

function setupCollapsibleSections() {
  document.querySelectorAll('.collapsible-section').forEach((section, index) => {
    const toggle = section.querySelector('.collapsible-toggle');
    const content = section.querySelector('.collapsible-content');
    if (!toggle || !content) return;

    const storageKey = getCollapsibleStorageKey(section, index);
    const stored = localStorage.getItem(storageKey);

    const setExpanded = (expanded, { persist = true } = {}) => {
      section.dataset.collapsed = expanded ? 'false' : 'true';
      toggle.setAttribute('aria-expanded', String(expanded));
      content.hidden = !expanded;
      if (persist) {
        try { localStorage.setItem(storageKey, String(expanded)); } catch (_) {}
      }
    };

    // Defaultní stav: preferujeme uloženou hodnotu, jinak podle data-collapsed
    const initialExpanded = stored !== null
      ? stored === 'true'
      : section.dataset.collapsed === 'false';
    setExpanded(initialExpanded, { persist: false });

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      setExpanded(!isExpanded);
      // Při sbalení/rozbalení nemusíme přepočítávat, jen měníme viditelnost
    });
  });

  // Pomocné: aktualizuj počet "aktivních" voleb v summary značce u "Dokončování"
  updateFinishingCount();
  document.addEventListener('change', (e) => {
    if (e.target.closest('#finishing-collapsible')) updateFinishingCount();
  });
}

function updateFinishingCount() {
  const counter = document.getElementById('finishing-count');
  if (!counter) return;
  const ids = ['lakovani', 'laminace', 'falcovani', 'bigovani', 'vrtani', 'perforace', 'lepeni', 'vysek', 'predsek', 'razba'];
  let active = 0;
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el && el.value && el.value !== '0' && el.value !== '') active += 1;
  });
  counter.textContent = active > 0 ? String(active) : '';
  counter.classList.toggle('hidden', active === 0);
}

// ──────────────────────────────────────────────
// Sidebar – collapsible (ikonky / rozbalený) s persistencí do localStorage.
// Není vázáno na přihlášení – inicializuje se hned po DOM ready, ale sidebar
// je skrytý (hidden) dokud ho unlockApp() neodemkne. Tlačítko funguje až pak.
// ──────────────────────────────────────────────
const SIDEBAR_COLLAPSED_KEY = 'tria:sidebar:collapsed';

function setupSidebarCollapse() {
  const sidebar = document.getElementById('app-sidebar');
  const btn = document.getElementById('sidebar-collapse-btn');
  if (!sidebar || !btn) return;

  let collapsed = false;
  try {
    collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch (_) {}
  sidebar.classList.toggle('sidebar-collapsed', collapsed);
  btn.setAttribute('aria-expanded', String(!collapsed));

  btn.addEventListener('click', () => {
    collapsed = !sidebar.classList.contains('sidebar-collapsed');
    sidebar.classList.toggle('sidebar-collapsed', collapsed);
    btn.setAttribute('aria-expanded', String(!collapsed));
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed)); } catch (_) {}
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupSidebarCollapse);
} else {
  setupSidebarCollapse();
}

// ──────────────────────────────────────────────
// Mobilní stavový bar – tlačítko „K výsledku" odscrolluje k sekci Výsledek kalkulace.
// ──────────────────────────────────────────────
function setupMobileResultScroll() {
  const btn = document.getElementById('vysledekMobilScroll');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const target = document.getElementById('vysledek-kalkulace');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupMobileResultScroll);
} else {
  setupMobileResultScroll();
}
